/* global Word, OfficeExtension */
/**
 * Camada de acesso ao documento (Office.js).
 *
 * Cada função executa uma operação real no Word e é registrada em `registry`,
 * que mapeia o `name` da ferramenta (ver backend/app/tools/definitions.py) para
 * o handler. O agent loop chama `executeTool(name, input)`.
 *
 * Princípios de robustez:
 *  - Falhas "esperadas" (nada selecionado, texto não encontrado) lançam Error
 *    com mensagem clara — o modelo lê o erro e se adapta.
 *  - Cores e estilos são normalizados (nomes PT/EN -> valor que o Word aceita).
 *  - PADRÃO NORVEN: por default todo conteúdo segue a house style (ver
 *    houseStyle.ts). O modelo escolhe o estilo pelo nome (styleRef) e o código
 *    aplica os valores exatos da marca — formatação determinística.
 */
import { isStyleRef, needsUpper, applyHouseParagraphStyle, applyHouseTableStyle, listKindOfRef, styleText, } from './houseStyle';
// Listas nativas da marca: a decisão pura (níveis, marcador digitado) e a
// execução no Word.
import { groupListRuns, insertListKind, levelIndexFromInput, normalizeLevels, parseListItems, punctuateListItems, stripTypedListMarker, typedListMarker, } from './listPlan';
import { ensureOutOfList, listKindLabel, makeHouseList, markersSummary, readListStates, removeTypedMarkers, } from './lists';
// Trocar o estilo de um parágrafo existente apaga negrito/itálico do texto.
import { restyleExisting } from './restyle';
import { clipCommentText, pageOfThreads } from './commentPlan';
import { coverSheetLeftovers } from './formatPlan';
// Estado do plano de tópicos vive em skeleton.ts (nenhum import de volta para
// cá nem para o taskpane, então não há ciclo).
import { setSkeleton } from './skeleton';
// ONDE escrever (seção, tabela, vazio exigido) — a decisão pura, testada fora do Word.
import { enclosingHeadingIndex, planAfter, planBefore, planEndOfSection, sectionEndIndex, } from './sectionPlan';
// Bytes das imagens anexadas no chat (memória do painel) — origem do insert_image.
import { findInsertableImage, listInsertableImages } from '../util/attachments';
// ======================================================================
// Helpers
// ======================================================================
const NAMED_COLORS = {
    vermelho: '#FF0000', red: '#FF0000',
    azul: '#0000FF', blue: '#0000FF',
    verde: '#008000', green: '#008000',
    preto: '#000000', black: '#000000',
    branco: '#FFFFFF', white: '#FFFFFF',
    amarelo: '#FFFF00', yellow: '#FFFF00',
    laranja: '#FFA500', orange: '#FFA500',
    roxo: '#800080', purple: '#800080',
    cinza: '#808080', cinzento: '#808080', gray: '#808080', grey: '#808080',
    rosa: '#FFC0CB', pink: '#FFC0CB',
    norven: '#550899',
};
/** Normaliza cor: nome PT/EN -> hex; garante '#' em hex de 6 dígitos. */
function normalizeColor(c) {
    if (!c)
        return undefined;
    const k = c.trim().toLowerCase();
    if (NAMED_COLORS[k])
        return NAMED_COLORS[k];
    if (/^#?[0-9a-f]{6}$/i.test(k))
        return k.startsWith('#') ? k : `#${k}`;
    return c; // deixa o Word tentar interpretar (ex.: nome CSS em inglês)
}
// CHAVES do enum, não os valores: o namespace global `Word` só existe depois
// que o office.js carrega o bundle do host, e este módulo é avaliado antes
// disso (ver a nota em houseStyle.ts). O valor é resolvido dentro da função,
// que só roda com o Office já pronto.
const BUILTIN_STYLES = {
    normal: 'normal',
    title: 'title',
    subtitle: 'subtitle',
    heading1: 'heading1',
    heading2: 'heading2',
    heading3: 'heading3',
    heading4: 'heading4',
    quote: 'quote',
    intensequote: 'intenseQuote',
    listparagraph: 'listParagraph',
};
function mapBuiltInStyle(name) {
    var _a;
    const key = (_a = BUILTIN_STYLES[name.replace(/\s+/g, '').toLowerCase()]) !== null && _a !== void 0 ? _a : 'normal';
    return Word.BuiltInStyleName[key];
}
function mapAlignment(a) {
    switch (a) {
        case 'centered': return Word.Alignment.centered;
        case 'right': return Word.Alignment.right;
        case 'justified': return Word.Alignment.justified;
        default: return Word.Alignment.left;
    }
}
function applyFontPatch(font, patch) {
    if (patch.name !== undefined)
        font.name = patch.name;
    if (patch.size !== undefined)
        font.size = patch.size;
    if (patch.bold !== undefined)
        font.bold = patch.bold;
    if (patch.italic !== undefined)
        font.italic = patch.italic;
    if (patch.underline !== undefined) {
        font.underline = patch.underline ? Word.UnderlineType.single : Word.UnderlineType.none;
    }
    const color = normalizeColor(patch.color);
    if (color !== undefined)
        font.color = color;
    const hl = normalizeColor(patch.highlightColor);
    if (hl !== undefined)
        font.highlightColor = hl;
}
/** Mensagem amigável a partir de um erro do Office.js. */
function errMessage(e) {
    var _a;
    if (e instanceof OfficeExtension.Error) {
        // debugInfo aponta a OPERAÇÃO que falhou no sync (ex.: Paragraph.startNewList)
        // — sem isso um GeneralException é indiagnosticável nos logs.
        const loc = ((_a = e.debugInfo) === null || _a === void 0 ? void 0 : _a.errorLocation) ? ` (em ${e.debugInfo.errorLocation})` : '';
        return `${e.code}: ${e.message}${loc}`;
    }
    if (e instanceof Error)
        return e.message;
    return String(e);
}
function normalizeRegionText(text) {
    return text
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/^\s*\d+(?:\.\d+)*\.?\s*/, '')
        .replace(/[:：]\s*$/, '')
        .replace(/\s+/g, ' ')
        .trim();
}
const COVER_CONTROL_TAGS = new Set(['document_type', 'document_title', 'client_name', 'document_date']);
const COVER_FIELD_TEXTS = new Set([
    'tipo de documento',
    'tipo do documento',
    'titulo do documento',
    'titulo do relatorio',
    'nome da area ou empresa',
    'nome da empresa',
    'nome do cliente',
]);
function isCoverControl(tag, title) {
    const t = (tag || '').trim();
    if (COVER_CONTROL_TAGS.has(t))
        return true;
    const label = normalizeRegionText(title || '');
    return COVER_FIELD_TEXTS.has(label);
}
function isCoverFieldText(text) {
    return COVER_FIELD_TEXTS.has(normalizeRegionText(text || ''));
}
function styleKey(styleBuiltIn) {
    return styleBuiltIn.toLowerCase().replace(/\s+/g, '');
}
function normalizeFontName(fontName) {
    return (fontName || '')
        .toLowerCase()
        .replace(/["']/g, '')
        .replace(/\s+/g, ' ')
        .trim();
}
function isStyreneCoverFont(fontName) {
    const f = normalizeFontName(fontName);
    return f === 'styrene a' || f.startsWith('styrene a ');
}
function isAllowedCoverSize(fontSize, allowed) {
    if (fontSize === undefined)
        return false;
    return allowed.some((size) => Math.abs(fontSize - size) <= 0.25);
}
function normalizeHexColor(color) {
    const c = (color || '').trim().toUpperCase();
    if (/^[0-9A-F]{6}$/.test(c))
        return `#${c}`;
    return c;
}
function hexToRgb(color) {
    const normalized = normalizeHexColor(color);
    const match = normalized.match(/^#([0-9A-F]{6})$/);
    if (!match)
        return null;
    const value = match[1];
    return {
        r: parseInt(value.slice(0, 2), 16),
        g: parseInt(value.slice(2, 4), 16),
        b: parseInt(value.slice(4, 6), 16),
    };
}
function isDarkCoverColor(color) {
    const c = normalizeHexColor(color);
    if (!c)
        return true;
    if (c === '#1B1B1B' || c === '#000000' || c === '#3C3C3C')
        return true;
    const rgb = hexToRgb(c);
    if (!rgb)
        return false;
    const values = [rgb.r, rgb.g, rgb.b];
    const max = Math.max(...values);
    const min = Math.min(...values);
    return max <= 90 && max - min <= 12;
}
function isPurpleCoverColor(color) {
    const c = normalizeHexColor(color);
    return !c || c === '#550899';
}
/**
 * Estilos da capa. No template vigente os campos vivem em caixas de texto (que
 * não entram em `body.paragraphs`), mas em documento trazido pelo usuário — ou
 * no fluxo "Formatar" — esses estilos aparecem como parágrafos de corpo, e não
 * podem ser confundidos com conteúdo. Os tamanhos aceitos cobrem o template
 * vigente (título 24) e os anteriores (22).
 */
const COVER_TYPE_AND_CLIENT_STYLES = new Set([
    'subtitle',
    'subtitulo',
    'subtítulo',
    'tipodedocumento',
    'cliente',
    'nomedocliente',
]);
function isCoverStyledParagraph(styleBuiltIn, fontName, fontSize, fontColor) {
    const key = styleKey(styleBuiltIn);
    if ((key === 'title' || key === 'titulo' || key === 'título') &&
        isStyreneCoverFont(fontName) &&
        isAllowedCoverSize(fontSize, [24, 22]) &&
        isPurpleCoverColor(fontColor)) {
        return true;
    }
    if (COVER_TYPE_AND_CLIENT_STYLES.has(key) &&
        isStyreneCoverFont(fontName) &&
        isAllowedCoverSize(fontSize, [20, 16]) &&
        isDarkCoverColor(fontColor)) {
        return true;
    }
    return false;
}
function protectedRegionMessage(region) {
    if (region === 'cover') {
        return 'A capa é região protegida. Para campos da capa, use fill_content_control; para conteúdo do documento, use uma âncora de tópico real do corpo.';
    }
    if (region === 'table_of_contents') {
        return 'O Sumário é região protegida. Não escreva conteúdo nele; atualize-o com update_document_fields a partir dos headings reais do corpo.';
    }
    return '';
}
/**
 * Resolve o parágrafo "âncora": escolhe a melhor ocorrência de `anchorText`.
 * Preferimos títulos reais do corpo e evitamos entradas de Sumário/TOC quando
 * houver alternativa — assim "Objetivo" no sumário não recebe conteúdo do POP.
 *
 * Busca case-insensitive (mais tolerante à caixa do título informado pelo
 * modelo). Lança erro claro e ACIONÁVEL se não achar, para o modelo reler e
 * corrigir o trecho em vez de repetir a mesma tentativa.
 */
async function resolveAnchorParagraph(context, anchorText) {
    const results = context.document.body.search(anchorText, { matchCase: false });
    results.load('items');
    await context.sync();
    if (results.items.length === 0) {
        throw new Error(`Âncora não encontrada: "${anchorText}". Releia o documento com ` +
            `read_document_text e use um trecho EXATO que exista nele (ex.: o título da seção).`);
    }
    const candidates = results.items.map((result) => result.paragraphs.getFirst());
    const regions = await loadParagraphRegions(context, candidates);
    const bodyIdx = candidates.map((_, i) => i).filter((i) => regions[i] === 'body');
    if (bodyIdx.length === 0) {
        throw new Error(`Âncora "${anchorText}" encontrada apenas em região protegida (capa ou Sumário). ` +
            'Releia o documento e use um título real do corpo como anchorText.');
    }
    const headingIdx = bodyIdx.filter((i) => headingLevel(candidates[i].style || '', String(candidates[i].styleBuiltIn || '')) !== null);
    // Sem ambiguidade: exatamente um título (alvo mais provável) ou exatamente
    // uma ocorrência de corpo — usa direto.
    if (headingIdx.length === 1)
        return candidates[headingIdx[0]];
    if (headingIdx.length === 0 && bodyIdx.length === 1)
        return candidates[bodyIdx[0]];
    // AMBÍGUA: N títulos iguais, ou N ocorrências de corpo sem título. Escolher
    // silenciosamente (comportamento antigo) acertava por sorte — errar aqui
    // insere conteúdo na seção errada sem ninguém perceber. Falha instrutiva:
    // lista as opções para o modelo escolher uma âncora mais específica.
    const ambiguous = headingIdx.length > 1 ? headingIdx : bodyIdx;
    const sample = ambiguous.slice(0, 4);
    sample.forEach((i) => candidates[i].load('text'));
    await context.sync();
    const options = sample.map((i) => `«${briefText(candidates[i].text, 60)}»`).join(', ');
    throw new Error(`Âncora ambígua: "${anchorText}" aparece em ${ambiguous.length} lugares do corpo: ` +
        `${options}. Use um trecho mais específico e único (ex.: o título completo ` +
        'com numeração, ou uma frase inteira daquela seção).');
}
/** InsertLocation a partir de anchorPosition ('before' | 'after'; padrão after). */
function anchorLoc(pos) {
    return pos === 'before' ? Word.InsertLocation.before : Word.InsertLocation.after;
}
/** Trecho curto de texto para exibição em retornos de ferramenta. */
function briefText(text, max = 70) {
    const clean = (text || '').replace(/\s+/g, ' ').trim();
    return clean.length <= max ? clean : `${clean.slice(0, max)}…`;
}
/**
 * "Micro-diff" do resultado de uma escrita: o parágrafo afetado + vizinhos.
 * Anexado ao retorno das ferramentas de escrita para o modelo REVISAR o efeito
 * no próprio tool_result, sem gastar uma releitura — transforma a instrução
 * "releia depois de editar" (que dependia de obediência) em verificação de
 * fato, com custo ~zero. Best-effort: nunca falha a escrita por causa disso.
 */
async function resultNeighborhood(context, para) {
    try {
        const prev = para.getPreviousOrNullObject();
        const next = para.getNextOrNullObject();
        para.load('text');
        prev.load('text,isNullObject');
        next.load('text,isNullObject');
        await context.sync();
        const lines = [
            prev.isNullObject ? '(início do documento)' : `  ${briefText(prev.text)}`,
            `► ${briefText(para.text)}`,
            next.isNullObject ? '(fim do documento)' : `  ${briefText(next.text)}`,
        ];
        return `\nVizinhança resultante (revise se ficou no lugar certo):\n${lines.join('\n')}`;
    }
    catch (_a) {
        return '';
    }
}
/** Rótulo humano de onde a inserção caiu (entra no retorno p/ o modelo revisar). */
function anchorWhere(anchorText, pos) {
    if (pos === 'endOfSection')
        return `no fim da seção "${anchorText}"`;
    return `${pos === 'before' ? 'antes' : 'depois'} de "${anchorText}"`;
}
/** Carrega o corpo inteiro numa leitura só (texto, estilo, tabela). */
async function loadBodyMap(context) {
    const ps = context.document.body.paragraphs;
    ps.load('items/text,items/style,items/styleBuiltIn,items/tableNestingLevel');
    await context.sync();
    return {
        items: ps.items,
        shapes: ps.items.map((p) => {
            var _a;
            return ({
                level: headingLevel(p.style || '', String(p.styleBuiltIn || '')),
                inTable: ((_a = p.tableNestingLevel) !== null && _a !== void 0 ? _a : 0) > 0,
                empty: !(p.text || '').trim(),
            });
        }),
    };
}
/**
 * Posição de `target` no corpo (−1 se não der para afirmar). Pelo texto quando
 * ele é único; entre textos repetidos, pela comparação de intervalos do Word —
 * nunca "o primeiro que casar", que é como se insere no lugar errado.
 */
async function indexOfParagraph(context, map, target) {
    target.load('text');
    await context.sync();
    const candidatos = map.items.map((_, i) => i).filter((i) => map.items[i].text === target.text);
    if (candidatos.length === 1)
        return candidatos[0];
    if (!candidatos.length)
        return -1;
    const relacoes = candidatos.map((i) => target.getRange('Whole').compareLocationWith(map.items[i].getRange('Whole')));
    await context.sync();
    const k = relacoes.findIndex((r) => String(r.value) === 'Equal');
    return k >= 0 ? candidatos[k] : -1;
}
/** Parágrafo novo e vazio no estilo de corpo — o vazio exigido depois de tabela. */
function insertBlankBodyParagraph(ref, loc) {
    const blank = ref.insertParagraph('', loc);
    applyHouseParagraphStyle(blank, 'body_paragraph');
    return blank;
}
/** Traduz o plano (índices) em Office.js. */
async function spotFromPlan(context, map, plan, where, note) {
    const depois = (p) => (t) => p.insertParagraph(t, Word.InsertLocation.after);
    switch (plan.kind) {
        case 'reuse': {
            const p = map.items[plan.index];
            // "Vazio" pelo texto pode ser uma imagem ou uma quebra de página: só o
            // OOXML diz. Estrutural não se reaproveita — escreve depois dele.
            const ooxml = p.getOoxml();
            await context.sync();
            if (isStructuralParagraph(ooxml.value || ''))
                return { make: depois(p), where, note };
            return {
                make: (t) => {
                    if (t)
                        p.insertText(t, Word.InsertLocation.end);
                    return p;
                },
                where,
                note,
            };
        }
        case 'after':
            return { make: depois(map.items[plan.index]), where, note };
        case 'before':
            return {
                make: (t) => map.items[plan.index].insertParagraph(t, Word.InsertLocation.before),
                where,
                note,
            };
        case 'afterTable': {
            if (plan.blankIndex !== null)
                return { make: depois(map.items[plan.blankIndex]), where, note };
            // A tabela não tem o vazio que a norma exige depois dela: ele nasce
            // agora, e o conteúdo novo vem depois dele — nunca colado na tabela.
            const tabela = map.items[plan.tableEnd].parentTable;
            return {
                make: (t) => insertBlankBodyParagraph(tabela, 'After').insertParagraph(t, Word.InsertLocation.after),
                where,
                note,
            };
        }
        case 'beforeTable': {
            // Antes da TABELA — inserir "antes" do parágrafo da 1ª célula escreveria
            // dentro dela.
            const tabela = map.items[plan.tableStart].parentTable;
            return {
                make: (t) => tabela.insertParagraph(t, Word.InsertLocation.before),
                where,
                note,
            };
        }
    }
}
/**
 * O lugar de uma inserção ancorada em `anchorText`.
 *
 *  - `before` / `after`: em volta do parágrafo — reaproveitando uma linha vazia
 *    que sobrou ali, EXCETO o vazio exigido depois de tabela; âncora dentro de
 *    tabela vale pela tabela inteira (antes/depois dela, nunca na célula);
 *  - `endOfSection`: depois do último parágrafo da seção que a âncora abre (ou
 *    em que está);
 *  - um TÍTULO novo de nível igual ou maior que o da âncora, pedido `after`,
 *    vai para o FIM da seção: logo abaixo do título ele tomaria o conteúdo da
 *    seção (o novo tópico "rouba" o texto do anterior — defeito visto pelo QA).
 */
async function resolveAnchoredSpot(context, anchorText, position, opts = {}) {
    var _a;
    const anchor = await resolveAnchorParagraph(context, anchorText);
    const map = await loadBodyMap(context);
    const idx = await indexOfParagraph(context, map, anchor);
    let where = anchorWhere(anchorText, position);
    if (idx < 0) {
        // Não deu para situar a âncora no corpo: o comportamento antigo, sem seção.
        return {
            make: (t) => anchor.insertParagraph(t, anchorLoc(position)),
            where,
            note: '',
        };
    }
    const nivelAncora = map.shapes[idx].inTable ? null : map.shapes[idx].level;
    const nivelNovo = (_a = opts.newHeadingLevel) !== null && _a !== void 0 ? _a : null;
    let plan;
    let note = '';
    if (position === 'endOfSection') {
        const secao = nivelAncora !== null && nivelAncora >= 1
            ? { index: idx, level: nivelAncora }
            : enclosingHeadingIndex(map.shapes, idx);
        // Sem título antes: o "fim da seção" é o fim do corpo (nível −1: nenhum
        // título o encerra).
        plan = secao
            ? planEndOfSection(map.shapes, secao.index, secao.level)
            : planEndOfSection(map.shapes, idx, -1);
    }
    else if (position !== 'before' &&
        nivelNovo !== null &&
        nivelAncora !== null &&
        nivelAncora >= 1 &&
        nivelNovo <= nivelAncora) {
        plan = planEndOfSection(map.shapes, idx, nivelNovo);
        where = anchorWhere(anchorText, 'endOfSection');
        note =
            ` (Coloquei no FIM da seção, e não logo abaixo do título: um título de mesmo nível ali ` +
                `deixaria "${briefText(anchorText, 60)}" vazia e tomaria o conteúdo dela.)`;
    }
    else if (position === 'before') {
        plan = planBefore(map.shapes, idx);
    }
    else {
        plan = planAfter(map.shapes, idx);
        // Logo abaixo do título de uma seção que já tem conteúdo é o COMEÇO dela.
        // Pode ser o pedido ("no começo do tópico") — mas, visto com o modelo real,
        // "no final do tópico" também chegava assim. O retorno diz onde caiu e como
        // pedir o fim, para a revisão do próprio modelo corrigir.
        if (nivelAncora !== null &&
            nivelAncora >= 1 &&
            sectionEndIndex(map.shapes, idx, nivelAncora) > idx) {
            note =
                ' (Ficou no COMEÇO da seção, logo abaixo do título, antes do conteúdo dela. Se o ' +
                    "pedido era no final do tópico, use anchorPosition 'endOfSection'.)";
        }
    }
    return spotFromPlan(context, map, plan, where, note);
}
/** Nível (1–4) de um `styleRef` de título, `null` para os demais. */
function headingLevelOfRef(ref) {
    const m = /^heading_([1-4])$/.exec(ref !== null && ref !== void 0 ? ref : '');
    return m ? Number(m[1]) : null;
}
/**
 * Garante o parágrafo vazio que a norma exige DEPOIS de uma tabela. A tabela
 * inserida entre dois parágrafos ficava colada no seguinte; aqui o vazio nasce
 * junto. Best-effort: a faxina de fim de turno cobre o que escapar.
 */
async function ensureBlankAfterTable(context, table) {
    var _a;
    try {
        const depois = table.getParagraphAfterOrNullObject();
        depois.load('isNullObject,text,tableNestingLevel');
        await context.sync();
        if (depois.isNullObject) {
            insertBlankBodyParagraph(table, 'After');
        }
        else if ((depois.text || '').trim() || ((_a = depois.tableNestingLevel) !== null && _a !== void 0 ? _a : 0) > 0) {
            insertBlankBodyParagraph(depois, 'Before');
        }
        else {
            return;
        }
        await context.sync();
    }
    catch (e) {
        console.warn('[ensureBlankAfterTable] ignorado (a faxina cobre):', e);
    }
}
// ======================================================================
// Leitura
// ======================================================================
/** Nível de título a partir do nome do estilo (built-in ou localizado).
 *  Title/Título → 0; Heading numerado → N; outro título sem número → 1; não-título → null. */
function isTocStyle(styleName, styleBuiltIn) {
    const s = `${styleName} ${styleBuiltIn}`.toLowerCase();
    return /\btoc\b|sum[aá]rio/.test(s);
}
function paragraphRegion(styleName, styleBuiltIn, text = '', ccTag = '', ccTitle = '', fontName = '', fontSize, fontColor = '') {
    if (isTocStyle(styleName, styleBuiltIn) || normalizeRegionText(text) === 'sumario') {
        return 'table_of_contents';
    }
    if (isCoverStyledParagraph(styleBuiltIn, fontName, fontSize, fontColor))
        return 'cover';
    if (isCoverControl(ccTag, ccTitle) || isCoverFieldText(text))
        return 'cover';
    return 'body';
}
export async function loadParagraphRegions(context, paragraphs) {
    const controls = [];
    paragraphs.forEach((p) => {
        p.load('text,style,styleBuiltIn,font/name,font/size,font/color');
        try {
            const cc = p.parentContentControlOrNullObject;
            cc.load('tag,title,isNullObject');
            controls.push(cc);
        }
        catch (_a) {
            controls.push(null);
        }
    });
    await context.sync();
    const coverByStyle = [];
    const regions = paragraphs.map((p, i) => {
        const cc = controls[i];
        const tag = cc && !cc.isNullObject ? cc.tag || '' : '';
        const title = cc && !cc.isNullObject ? cc.title || '' : '';
        const size = typeof p.font.size === 'number' ? p.font.size : undefined;
        const color = p.font.color ? String(p.font.color) : '';
        const region = paragraphRegion(p.style || '', String(p.styleBuiltIn || ''), p.text || '', tag, title, p.font.name || '', size, color);
        coverByStyle.push(region === 'cover' &&
            !isCoverControl(tag, title) &&
            !isCoverFieldText(p.text || '') &&
            isCoverStyledParagraph(String(p.styleBuiltIn || ''), p.font.name || '', size, color));
        return region;
    });
    regions.coverByStyle = coverByStyle;
    return regions;
}
async function assertEditableParagraph(context, paragraph, action = 'Ação') {
    // Mesma regra de região das ferramentas por índice (ver regionsWithPrologue).
    const [region] = await regionsWithPrologue(context, [paragraph]);
    if (region !== 'body')
        throw new Error(`${action} bloqueada. ${protectedRegionMessage(region)}`);
}
async function assertEditableLocation(context, loc, action) {
    if (loc === 'startOfDocument') {
        await assertEditableParagraph(context, context.document.body.paragraphs.getFirst(), action);
    }
    else if (loc === 'afterSelection' || loc === 'replaceSelection') {
        await assertEditableParagraph(context, context.document.getSelection().paragraphs.getFirst(), action);
    }
}
/**
 * O parágrafo é ESTRUTURAL do template (não uma linha em branco qualquer)?
 * Lido do OOXML — a marcação real —, porque `paragraph.text` não expõe isso:
 * foi exatamente essa confusão que fez uma versão anterior escrever dentro de
 * uma sobrecapa. Dois sinais bastam:
 *
 *  - **quebra de página**, como run (`<w:br w:type="page"/>`) ou como
 *    propriedade (`<w:pageBreakBefore/>`): escrever num parágrafo com quebra
 *    põe o texto ANTES dela, ou seja, na página anterior;
 *  - **desenho ancorado** (`<w:drawing>`): a capa da Proposta Técnica são
 *    caixas de texto ancoradas em parágrafos vazios, e documentos feitos com o
 *    template ANTIGO ainda trazem duas sobrecapas de imagem ancoradas do mesmo
 *    jeito, sem quebra entre elas. Sem este segundo sinal, um parágrafo "vazio"
 *    que na verdade sustenta a capa pareceria disponível.
 *
 * No template atual a fronteira capa/corpo é a quebra de página do último
 * parágrafo da capa. Ela é o PRIMEIRO sinal, e é obrigatória: sem ela o corpo
 * renderiza na página 1, por cima da capa (ver `test_cover_template.py`).
 */
export function isStructuralParagraph(ooxml) {
    const corpo = paragraphBodyOoxml(ooxml);
    return (/<w:br[^>]*w:type="page"/i.test(corpo) ||
        /<w:pageBreakBefore\b/i.test(corpo) ||
        /<w:drawing\b/i.test(corpo) ||
        hasSectionBreak(corpo));
}
/**
 * O parágrafo ENCERRA uma seção (`<w:sectPr>` dentro do `<w:pPr>` dele)? É a
 * fronteira da capa quando o template entra como seção própria na frente de um
 * documento existente (ver `formatIntoTemplate`). O `<w:sectPr>` que todo
 * `<w:body>` traz no fim, filho direto do body, não conta — ele está em todo
 * pacote de `getOoxml()`.
 */
function hasSectionBreak(corpo) {
    return /<w:pPr\b[^>]*>(?:(?!<\/w:pPr>)[\s\S])*<w:sectPr\b/i.test(corpo);
}
/**
 * O parágrafo em si, dentro do pacote que `getOoxml()` devolve.
 *
 * `Paragraph.getOoxml()` não devolve o parágrafo: devolve um PACOTE OOXML com
 * estilos, numeração, tema e — no parágrafo que carrega as propriedades de
 * seção, o último do corpo — cabeçalho e rodapé. O cabeçalho da Norven tem o
 * logotipo, um `<w:drawing>`: procurar no pacote inteiro marcava esse parágrafo
 * como "âncora de capa" e o copiloto pulava a primeira linha do corpo, escrevendo
 * uma linha abaixo na página 2. Fora do pacote (trechos de teste), devolve o
 * próprio texto.
 */
function paragraphBodyOoxml(ooxml) {
    const parte = /pkg:name="\/word\/document\.xml"[\s\S]*?<\/pkg:part>/i.exec(ooxml);
    const corpo = parte && /<w:body>([\s\S]*)<\/w:body>/i.exec(parte[0]);
    return corpo ? corpo[1] : ooxml;
}
/**
 * Teto da varredura do prólogo. A capa do template atual ocupa 10 parágrafos
 * (o template antigo, com sobrecapas, ocupava 12); 40 cobre com folga qualquer
 * template futuro sem tornar a leitura de OOXML cara.
 */
const PROLOGUE_SCAN_MAX = 40;
/**
 * Índice do PRIMEIRO parágrafo de conteúdo do corpo — o fim do prólogo (a capa,
 * mais as sobrecapas em documentos do template antigo). `0` significa
 * "documento sem prólogo". No template atual, é a linha `Corpo` no topo da
 * página 2.
 *
 * Por que isto precisa existir: no template da Proposta Técnica a capa NÃO está
 * no corpo — ela vive em caixas de texto flutuantes ancoradas em parágrafos de
 * corpo VAZIOS (e as sobrecapas antigas eram imagens ancoradas do mesmo jeito). Para o
 * Office.js esses parágrafos são corpo comum: `body.paragraphs.getFirst()` é a
 * primeira linha da CAPA, e `region` não os distingue (não têm estilo nem texto
 * de capa). Foi assim que um `insert_paragraph` em `startOfDocument` foi parar
 * na capa, acima de "Proposta Técnica".
 *
 * O prólogo é a sequência inicial de parágrafos VAZIOS que sustenta a capa: vai
 * até o ÚLTIMO parágrafo que carrega estrutura do template (quebra de página ou
 * desenho ancorado — ver `isStructuralParagraph`) e engole os vazios que vierem
 * depois dele, que são o espaço da capa. A primeira linha do corpo é o último
 * desses vazios (ou o parágrafo seguinte, quando a âncora fecha o prólogo).
 * Uma imagem no meio do documento não é prólogo: a varredura para no primeiro
 * parágrafo com texto.
 *
 * Degrada para `0` em qualquer falha: sem OOXML não há como afirmar que existe
 * prólogo, e travar a escrita por engano é pior do que não proteger.
 */
export async function coverPrologueEnd(context, items) {
    try {
        if (!items.length)
            return 0;
        // Só o RUN INICIAL de parágrafos vazios interessa.
        let scan = 0;
        while (scan < items.length && scan < PROLOGUE_SCAN_MAX && !(items[scan].text || '').trim()) {
            scan++;
        }
        if (scan === 0)
            return 0; // corpo já começa com texto: não há prólogo
        const head = items.slice(0, scan);
        const results = head.map((p) => p.getOoxml());
        await context.sync();
        const ooxml = results.map((r) => r.value || '');
        let lastStructural = -1;
        for (let i = 0; i < ooxml.length; i++) {
            if (isStructuralParagraph(ooxml[i]))
                lastStructural = i;
        }
        if (lastStructural < 0)
            return 0; // nenhuma âncora de capa: não há prólogo
        // Vazios DEPOIS da última âncora ainda são espaço da capa: são eles que
        // empurram o corpo para a página seguinte. O corpo começa no ÚLTIMO deles —
        // escrever no primeiro cairia no meio da capa. Foi o que aconteceu quando o
        // template foi salvo sem a quebra de página: o texto nascia na página 1,
        // sobre a capa, e sobravam linhas vazias na página 2.
        // Sem nenhum vazio depois da âncora (a quebra é o último parágrafo do
        // prólogo), o corpo é o parágrafo seguinte, como antes.
        return scan - 1 > lastStructural ? scan - 1 : scan;
    }
    catch (e) {
        console.warn('[coverPrologueEnd] OOXML indisponível; assumindo documento sem capa:', e);
        return 0;
    }
}
/**
 * Onde começa o conteúdo do documento. Espelho de
 * `resolveEndOfDocumentInsertion` para o outro extremo: `startOfDocument` NÃO
 * é o parágrafo 0 quando o template tem capa — é a primeira linha depois do
 * prólogo. Devolve `null` em documento sem prólogo (ex.: tipo "Outro"), e aí o
 * chamador segue com `InsertLocation.start` como sempre.
 */
async function resolveStartOfBodyContent(context) {
    const paragraphs = context.document.body.paragraphs;
    paragraphs.load('items/text');
    await context.sync();
    const items = paragraphs.items;
    if (!items.length)
        return null;
    const start = await coverPrologueEnd(context, items);
    if (start === 0)
        return null; // sem capa: comportamento clássico
    if (start >= items.length) {
        // O corpo termina no prólogo (template recém-aplicado sem linha de corpo):
        // abre a linha depois da última peça estrutural.
        return { anchor: items[items.length - 1], mode: 'after', skipped: start };
    }
    const target = items[start];
    return {
        anchor: target,
        mode: (target.text || '').trim() ? 'before' : 'reuse',
        skipped: start,
    };
}
/**
 * Escreve um parágrafo de texto no início do CONTEÚDO, respeitando o prólogo.
 * `reuse` aproveita a linha de corpo que o template reserva (não deixa linha em
 * branco antes do texto — mesmo cuidado do lado do `endOfDocument`).
 */
function insertParagraphAtBodyStart(start, text) {
    if (start.mode === 'reuse') {
        start.anchor.insertText(text, Word.InsertLocation.replace);
        return start.anchor;
    }
    return start.anchor.insertParagraph(text, start.mode === 'before' ? Word.InsertLocation.before : Word.InsertLocation.after);
}
/**
 * Onde encaixar um BLOCO (tabela, lista, imagem) em relação ao parágrafo de
 * início do conteúdo. Em `reuse` vai ANTES da linha vazia reservada: o bloco
 * abre a página e o vazio sobra depois dele — que é justamente o respiro que a
 * norma pede depois de tabela.
 */
function bodyStartInsertLocation(mode) {
    return mode === 'after' ? Word.InsertLocation.after : Word.InsertLocation.before;
}
/** Rótulo do destino, para o retorno da ferramenta dizer onde caiu. */
function bodyStartWhere(skipped) {
    return `startOfDocument (início do conteúdo — ${skipped} parágrafos da capa preservados)`;
}
/**
 * Marca como `cover` os parágrafos do PRÓLOGO (capa, e sobrecapas em documentos
 * do template antigo). Eles são
 * parágrafos de corpo vazios para o Office.js — sem estilo nem texto de capa,
 * `paragraphRegion` não tem como distingui-los. Sem isto, escrita por ÍNDICE
 * (`replace_paragraphs`) alcançaria a página 1, e `read_document_text` os
 * apresentaria ao modelo como corpo disponível.
 * `offset` é o índice global do primeiro item de `regions`.
 */
export function markPrologueAsCover(regions, prologueEnd, offset = 0) {
    if (prologueEnd <= 0)
        return regions;
    return regions.map((r, i) => {
        var _a;
        if (offset + i < prologueEnd)
            return 'cover';
        // Com capa de verdade (o prólogo), um parágrafo que só PARECE de capa pelo
        // estilo, depois dela, é conteúdo: a folha de rosto em texto de um
        // documento formatado ("Formatar"), que a revisão pode levar para a capa ou
        // apagar. Protegido, ninguém conseguiria tirá-la de lá.
        if (r === 'cover' && ((_a = regions.coverByStyle) === null || _a === void 0 ? void 0 : _a[i]))
            return 'body';
        return r;
    });
}
/**
 * Decide como materializar uma inserção em `endOfDocument`. O fim FÍSICO do
 * documento nem sempre é um ponto de corpo válido:
 *  - um documento com Sumário pode terminar dentro da região do índice —
 *    anexar ali despeja conteúdo de corpo na página do Sumário. Nenhum tipo
 *    atual cria sumário, mas documento trazido pelo usuário pode ter um;
 *  - o corpo pode terminar em até DOIS parágrafos vazios: o que o template
 *    reserva para o corpo MAIS o parágrafo final que o Word preserva do
 *    documento destino ao aplicar o template (`insertFileFromBase64`).
 *    Anexar depois deles empurra o conteúdo para baixo — era o "escreve na
 *    página certa, mas pula uma linha". Reaproveitamos o último vazio e, se o
 *    ANTERIOR a ele também for um vazio livre, sobem UM passo — nunca mais
 *    que isso (ver `isStructuralParagraph`).
 *
 * A checagem "é vazio" e a parada da varredura usam o **OOXML** do parágrafo,
 * não `paragraph.text` — já foi bug aqui duas vezes: `text` não expõe de forma
 * confiável a quebra de página (`\f`), e documentos do template antigo não
 * separam as sobrecapas por quebra (são imagens ancoradas em parágrafo vazio).
 * Sem OOXML como sinal de verdade, a varredura atravessava esses parágrafos e o
 * texto ia escrever dentro de uma sobrecapa. Detalhe em `isStructuralParagraph`.
 *
 * Se a leitura do OOXML falhar, degrada para reaproveitar só o ÚLTIMO parágrafo
 * (uma linha em branco sobrando é defeito cosmético; página errada não é).
 */
async function resolveEndOfDocumentInsertion(context) {
    try {
        const paragraphs = context.document.body.paragraphs;
        paragraphs.load('items');
        await context.sync();
        const items = paragraphs.items;
        if (!items.length)
            return { breakAfterToc: false };
        // Só o fim do documento interessa: o último parágrafo e UM candidato antes.
        const tail = items.slice(Math.max(0, items.length - 2));
        const regions = await loadParagraphRegions(context, tail);
        const lastIdx = tail.length - 1;
        if (regions[lastIdx] === 'table_of_contents')
            return { breakAfterToc: true };
        const isEmptyBody = (i) => regions[i] === 'body' && !(tail[i].text || '').trim();
        if (!isEmptyBody(lastIdx))
            return { breakAfterToc: false };
        // OOXML dos dois: única leitura confiável do que é estrutura do template.
        let ooxml = null;
        try {
            const results = tail.map((p) => p.getOoxml());
            await context.sync();
            ooxml = results.map((r) => r.value || '');
        }
        catch (e) {
            console.warn('[resolveEndOfDocumentInsertion] OOXML indisponível; reaproveito só o último:', e);
        }
        // Sem OOXML não há como andar para trás em segurança.
        if (!ooxml)
            return { reuse: tail[lastIdx], breakAfterToc: false };
        // Último parágrafo é estrutural (quebra ou imagem ancorada): não escreve
        // nele; anexa depois, como no fluxo clássico.
        if (isStructuralParagraph(ooxml[lastIdx]))
            return { breakAfterToc: false };
        // UM passo atrás, no máximo. Aplicar o template deixa DOIS vazios no fim (o
        // que o template reserva para o corpo + o parágrafo final que o Word
        // preserva do documento destino), e escrever no último produzia o "começa
        // na página certa, mas pula uma linha". Um passo resolve isso.
        //
        // Por que o teto é 1 e não "todo o run de vazios": a capa são caixas
        // ancoradas em parágrafos vazios, e documentos do template antigo trazem
        // sobrecapas ancoradas do mesmo jeito, sem quebra entre elas. Uma varredura
        // longa atravessa esses vazios e vai escrever dentro deles (bug já cometido
        // aqui duas vezes). Com um passo, o pior caso é uma linha em branco a mais,
        // nunca a página errada.
        const prevIdx = lastIdx - 1;
        if (prevIdx >= 0 && isEmptyBody(prevIdx) && !isStructuralParagraph(ooxml[prevIdx])) {
            return { reuse: tail[prevIdx], breakAfterToc: false };
        }
        return { reuse: tail[lastIdx], breakAfterToc: false };
    }
    catch (_a) {
        /* segue o fluxo clássico */
    }
    return { breakAfterToc: false };
}
async function preferBodyRanges(context, ranges) {
    if (ranges.length === 0)
        return ranges;
    const paragraphs = ranges.map((r) => r.paragraphs.getFirst());
    // Mesma regra de região das ferramentas por índice: a folha de rosto em texto
    // de um documento formatado, depois da capa de verdade, é corpo. Antes era
    // "protegida" para as ferramentas por busca e editável para as por índice.
    const regions = await regionsWithPrologue(context, paragraphs);
    const bodyRanges = ranges.filter((_, i) => regions[i] === 'body');
    if (bodyRanges.length)
        return bodyRanges;
    throw new Error('Trecho encontrado apenas em região protegida (capa ou Sumário). Use um trecho do corpo do documento como alvo.');
}
export function headingLevel(styleName, styleBuiltIn) {
    if (isTocStyle(styleName, styleBuiltIn))
        return null;
    const s = `${styleName} ${styleBuiltIn}`.toLowerCase();
    // "Subtítulo" contém "título": sem isto o subtítulo de uma folha de rosto
    // entrava no outline como tópico de nível 1.
    if (/subt[íi]tulo|subtitle/.test(s))
        return null;
    if (/\btitle\b|\bt[íi]tulo\b(?!\s*\d)/.test(s) && !/heading/.test(s))
        return 0;
    const m = s.match(/(?:heading|t[íi]tulo)\s*(\d)/);
    if (m)
        return parseInt(m[1], 10);
    if (/heading|t[íi]tulo/.test(s))
        return 1;
    return null;
}
export function isHeadingStyleRef(ref) {
    return ref === 'heading_1' || ref === 'heading_2' || ref === 'heading_3' || ref === 'heading_4';
}
/**
 * Atualiza campos dinâmicos do Word após mudanças estruturais.
 *
 * O caso principal é o Sumário: ele é um campo TOC gerado a partir dos headings.
 * Quando o mIAu insere/aplica heading, atualizamos esse campo aqui para o
 * usuário não precisar clicar manualmente em "Atualizar Campo".
 */
async function updateDynamicFields(context) {
    let updated = 0;
    const doc = context.document;
    const body = context.document.body;
    const collections = [body.fields, doc.fields].filter(Boolean);
    for (const fields of collections) {
        try {
            const tocFields = fields.getByTypes([Word.FieldType.toc]);
            tocFields.load('items,type');
            await context.sync();
            for (const field of tocFields.items) {
                field.updateResult();
                updated++;
            }
            await context.sync();
            if (updated > 0)
                return updated;
        }
        catch (_a) {
            // Word na Web pode expor campos como leitura; nesse caso apenas ignoramos.
        }
    }
    try {
        const tocs = doc.tableOfContents;
        if (tocs) {
            tocs.load('items');
            await context.sync();
            for (const toc of tocs.items) {
                toc.updatePageNumbers();
                updated++;
            }
            await context.sync();
            if (updated > 0)
                return updated;
        }
    }
    catch (_b) {
        // Nem todo host/superfície do Word suporta TableOfContentsCollection.
    }
    return updated;
}
export async function getDocumentOverview() {
    return Word.run(async (context) => {
        var _a;
        const body = context.document.body;
        body.load('text');
        const paragraphs = body.paragraphs;
        paragraphs.load('text,style,styleBuiltIn');
        const ccs = context.document.contentControls;
        ccs.load('tag,title');
        const tables = body.tables;
        tables.load('items');
        const inlinePics = body.inlinePictures;
        inlinePics.load('items');
        await context.sync();
        const floatingCount = await countFloatingPictures(context);
        const regions = await loadParagraphRegions(context, paragraphs.items);
        const wordCount = ((_a = body.text.match(/\S+/g)) !== null && _a !== void 0 ? _a : []).length;
        // Outline: títulos do documento (índice do parágrafo + texto + nível). É o
        // que permite ao modelo localizar "a seção 4" e ancorar inserções/edições
        // (anchorText) sem precisar ler o documento inteiro.
        const outline = paragraphs.items
            .map((p, index) => ({
            index,
            text: (p.text || '').trim(),
            level: headingLevel(p.style || '', String(p.styleBuiltIn || '')),
            region: regions[index],
        }))
            .filter((p) => p.region === 'body' && p.level !== null && p.text.length > 0);
        const tocEntries = paragraphs.items
            .map((p, index) => ({
            index,
            text: (p.text || '').trim(),
            style: p.style || '',
            styleBuiltIn: String(p.styleBuiltIn || ''),
            region: regions[index],
        }))
            .filter((p) => p.region === 'table_of_contents' && p.text.length > 0);
        // Do que o documento TRATA: o começo do 1º parágrafo de corpo que não é
        // título. Os títulos sozinhos ("Objetivo", "Escopo") não dizem o assunto, e
        // sem ele não há como julgar se um conteúdo tem relação com o documento.
        const lede = paragraphs.items.find((p, index) => regions[index] === 'body' &&
            (p.text || '').trim().length > 0 &&
            headingLevel(p.style || '', String(p.styleBuiltIn || '')) === null);
        // Comentários (RF08/RF09): sinalizados aqui para o modelo SABER que existem
        // threads a ler, sem precisar de uma chamada extra. Best-effort (host antigo).
        const commentStats = await countComments(context);
        return Object.assign(Object.assign({ paragraphCount: paragraphs.items.length, wordCount, tableCount: tables.items.length, imageCount: {
                inline: inlinePics.items.length,
                floating: floatingCount,
                note: 'Detalhes e imageIds via list_images.',
            }, hasTableOfContents: tocEntries.length > 0, tocEntries,
            outline }, (lede ? { lede: briefText(lede.text, 240) } : {})), { contentControls: ccs.items.map((c) => ({ tag: c.tag, title: c.title })), comments: {
                total: commentStats.total,
                unresolved: commentStats.unresolved,
                note: 'Threads (conteúdo + respostas + trecho ancorado) via list_comments.',
            }, pageCountNote: 'Office.js não expõe contagem de páginas.' });
    });
}
/** Conta comentários (total e abertos). 0/0 se o host não suportar. */
async function countComments(context) {
    if (!wordApi14Supported())
        return { total: 0, unresolved: 0 };
    try {
        const comments = context.document.body.getComments();
        comments.load('items/resolved');
        await context.sync();
        return {
            total: comments.items.length,
            unresolved: comments.items.filter((c) => !c.resolved).length,
        };
    }
    catch (_a) {
        return { total: 0, unresolved: 0 };
    }
}
/**
 * Teto de parágrafos por leitura. O `maxParagraphs` vem do MODELO e antes só
 * tinha piso (`Math.max(1, ...)`): um `maxParagraphs: 100000` devolvia o
 * documento inteiro num único `tool_result`. Medido num documento de 3.000
 * parágrafos, isso são ~267 mil tokens — acima da janela de 200k do modelo, ou
 * seja, o passo não fica caro: ele FALHA, e leva o turno junto.
 *
 * 400 é o dobro do default, cabe uma seção grande com folga e custa ~30k tokens
 * no pior caso. Quem precisa de mais continua paginando por `nextStartIndex`,
 * que a resposta já devolve.
 */
const READ_WINDOW_MAX_PARAGRAPHS = 400;
export async function readDocumentText(input) {
    return Word.run(async (context) => {
        var _a, _b;
        const paras = context.document.body.paragraphs;
        paras.load('text,style,styleBuiltIn,tableNestingLevel');
        await context.sync();
        const total = paras.items.length;
        // Leitura POR JANELA: `startIndex` permite navegar documentos grandes por
        // seção (o outline do overview traz o índice de cada título) em vez de
        // tudo-ou-nada a partir do início. Os índices retornados são GLOBAIS —
        // continuam válidos como alvo de replace_paragraphs e como startIndex.
        const start = Math.min(Math.max(0, (_a = input.startIndex) !== null && _a !== void 0 ? _a : 0), total);
        const pedido = Math.max(1, (_b = input.maxParagraphs) !== null && _b !== void 0 ? _b : 200);
        const max = Math.min(pedido, READ_WINDOW_MAX_PARAGRAPHS);
        const janelaReduzida = pedido > max;
        const visible = paras.items.slice(start, start + max);
        // Parágrafo de imagem tem texto vazio: sem esta marca, para o modelo ele era
        // uma linha em branco — e "apague o conteúdo do tópico" deixava a figura.
        const figuras = visible.map((p) => {
            const c = p.inlinePictures;
            c.load('items');
            return c;
        });
        await context.sync();
        // O marcador de lista nativa não está no `text`: sem isto, uma lista
        // numerada e uma de bullets pareciam corpo, e o modelo não tinha como
        // converter uma na outra nem preservar o nível de um subitem.
        const listas = await readListStates(context, visible);
        const prologueEnd = await coverPrologueEnd(context, paras.items);
        const regions = markPrologueAsCover(await loadParagraphRegions(context, visible), prologueEnd, start);
        const end = start + visible.length; // exclusivo
        const hasMore = end < total;
        return Object.assign(Object.assign(Object.assign({ paragraphCount: total, window: { startIndex: start, endIndexInclusive: Math.max(start, end - 1) }, truncated: hasMore }, (hasMore
            ? {
                nextStartIndex: end,
                note: (janelaReduzida
                    ? `A janela pedida (${pedido}) foi reduzida ao teto de ` +
                        `${READ_WINDOW_MAX_PARAGRAPHS} parágrafos por leitura — ler o documento ` +
                        'inteiro de uma vez estoura o contexto. '
                    : '') +
                    `O documento continua além desta janela (${total} parágrafos no total). ` +
                    `Para seguir lendo, chame de novo com startIndex=${end}. Para ir direto a ` +
                    'uma seção, use o índice do título (outline do contexto/get_document_overview) como startIndex.',
            }
            : {})), (visible.length === 0 && total > 0
            ? { note: `startIndex fora do intervalo: o documento tem ${total} parágrafos (índices 0..${total - 1}).` }
            : {})), { paragraphs: visible.map((p, i) => {
                var _a, _b;
                return (Object.assign(Object.assign(Object.assign({ index: start + i, text: p.text, style: p.style || '', styleBuiltIn: String(p.styleBuiltIn || ''), level: headingLevel(p.style || '', String(p.styleBuiltIn || '')), region: regions[i] }, (((_a = p.tableNestingLevel) !== null && _a !== void 0 ? _a : 0) > 0 ? { inTable: true } : {})), (figuras[i].items.length ? { images: figuras[i].items.length } : {})), (listas[i].inList
                    ? {
                        list: {
                            id: listas[i].listId,
                            kind: (_b = listas[i].kind) !== null && _b !== void 0 ? _b : 'other',
                            level: listas[i].level + 1,
                            marker: listas[i].marker,
                        },
                    }
                    : {})));
            }) });
    });
}
export async function readSelection() {
    return Word.run(async (context) => {
        const sel = context.document.getSelection();
        sel.load('text');
        sel.font.load('name,size,bold,italic');
        await context.sync();
        return {
            text: sel.text,
            font: {
                name: sel.font.name,
                size: sel.font.size,
                bold: sel.font.bold,
                italic: sel.font.italic,
            },
        };
    });
}
export async function searchText(input) {
    return Word.run(async (context) => {
        const results = context.document.body.search(input.query, { matchCase: !!input.matchCase });
        results.load('text');
        await context.sync();
        const limit = typeof input.maxResults === 'number' ? input.maxResults : 20;
        return {
            count: results.items.length,
            matches: results.items.slice(0, limit).map((r, i) => ({ index: i, text: r.text })),
        };
    });
}
export async function listContentControls() {
    return Word.run(async (context) => {
        const ccs = context.document.contentControls;
        ccs.load('tag,title,text');
        await context.sync();
        return ccs.items.map((c) => ({ tag: c.tag, title: c.title, text: c.text }));
    });
}
// ======================================================================
// Escrita: texto
// ======================================================================
export async function insertText(input) {
    return Word.run(async (context) => {
        var _a;
        let comentarios = SEM_COMENTARIOS;
        const loc = (_a = input.location) !== null && _a !== void 0 ? _a : 'replaceSelection';
        const body = context.document.body;
        await assertEditableLocation(context, loc, 'Inserção de texto');
        if (loc === 'endOfDocument') {
            // `insertText(end)` escreve DENTRO do último parágrafo; se o documento
            // termina no Sumário, isso corromperia o índice. Falha instrutiva.
            const placement = await resolveEndOfDocumentInsertion(context);
            if (placement.breakAfterToc) {
                throw new Error('O fim do documento está dentro do Sumário. Use insert_paragraph (abre ' +
                    'página própria automaticamente) ou uma âncora de corpo com anchorText.');
            }
            body.insertText(input.text, Word.InsertLocation.end);
        }
        else if (loc === 'startOfDocument') {
            // NUNCA o parágrafo 0 quando há capa: ele é a linha que ancora as caixas
            // de texto da capa, e escrever nele põe o texto na página 1.
            const start = await resolveStartOfBodyContent(context);
            if (start)
                insertParagraphAtBodyStart(start, input.text);
            else
                body.insertText(input.text, Word.InsertLocation.start);
        }
        else if (loc === 'afterSelection')
            context.document.getSelection().insertText(input.text, Word.InsertLocation.after);
        // Trocar a SELEÇÃO: clicar num comentário no painel do Word seleciona o
        // trecho dele, e "reescreva isso" caía aqui — `insertText(replace)` apagava
        // a thread sem aviso (medido). A troca por dentro a mantém.
        else
            comentarios = await replaceKeepingComments(context, context.document.getSelection(), input.text);
        await context.sync();
        return `Texto inserido (${loc}).${commentOutcomeNote(comentarios)}`;
    });
}
export async function insertParagraph(input) {
    return Word.run(async (context) => {
        var _a, _b;
        const loc = (_a = input.location) !== null && _a !== void 0 ? _a : 'endOfDocument';
        const body = context.document.body;
        // Padrão Norven: sem styleRef/style explícito, todo parágrafo é body_paragraph.
        const ref = isStyleRef(input.styleRef)
            ? input.styleRef
            : input.style
                ? 'body_paragraph' // legado tratado abaixo
                : 'body_paragraph';
        const useHouse = isStyleRef(input.styleRef) || !input.style;
        const listKind = useHouse ? listKindOfRef(ref) : null;
        let text = useHouse ? styleText(input.text, ref) : input.text;
        // Item de lista: o marcador é da lista — um "/" ou "1." digitado sairia dobrado.
        if (listKind)
            text = stripTypedListMarker(text);
        let para;
        let where;
        let nota = '';
        if (input.anchorText) {
            // Posicionamento ANCORADO: insere relativo ao parágrafo de referência, na
            // seção quando pedido (ou quando um título novo tomaria a seção), e
            // reaproveitando uma linha vazia que sobrou ali (ver resolveAnchoredSpot).
            const spot = await resolveAnchoredSpot(context, input.anchorText, input.anchorPosition, {
                newHeadingLevel: useHouse ? headingLevelOfRef(ref) : null,
            });
            para = spot.make(text);
            where = spot.where;
            nota = spot.note;
        }
        else if (loc === 'startOfDocument') {
            await assertEditableLocation(context, loc, 'Inserção de parágrafo');
            const start = await resolveStartOfBodyContent(context);
            if (start) {
                para = insertParagraphAtBodyStart(start, text);
                where = bodyStartWhere(start.skipped);
            }
            else {
                para = body.insertParagraph(text, Word.InsertLocation.start);
                where = loc;
            }
        }
        else if (loc === 'afterSelection' || loc === 'replaceSelection') {
            await assertEditableLocation(context, loc, 'Inserção de parágrafo');
            para = context.document.getSelection().insertParagraph(text, Word.InsertLocation.after);
            where = loc;
        }
        else {
            const placement = await resolveEndOfDocumentInsertion(context);
            if (placement.reuse) {
                // Reaproveita o parágrafo vazio final (ex.: a quebra de página que abre
                // o corpo no template) em vez de deixar linha em branco antes do texto.
                para = placement.reuse;
                para.insertText(text, Word.InsertLocation.end);
                where = 'endOfDocument (parágrafo vazio final reaproveitado)';
            }
            else {
                para = body.insertParagraph(text, Word.InsertLocation.end);
                if (placement.breakAfterToc) {
                    para.insertBreak(Word.BreakType.page, Word.InsertLocation.before);
                    where = 'endOfDocument (nova página aberta após o Sumário)';
                }
                else {
                    where = loc;
                }
            }
        }
        if (useHouse) {
            applyHouseParagraphStyle(para, ref);
        }
        else if (input.style) {
            // Caminho legado: estilo built-in livre solicitado explicitamente.
            para.styleBuiltIn = mapBuiltInStyle(input.style);
        }
        // Alinhamento explícito sempre tem prioridade (override do usuário).
        if (input.alignment)
            para.alignment = mapAlignment(input.alignment);
        await context.sync();
        let listaNota = '';
        if (listKind) {
            // Um item solto ao lado de uma lista do mesmo tipo entra NELA (a numeração
            // segue); sem vizinha, é uma lista nova de um item.
            const r = await makeHouseList(context, [para], listKind, [levelIndexFromInput(input.level)], {
                joinAdjacent: true,
            });
            if (input.alignment) {
                para.alignment = mapAlignment(input.alignment);
                await context.sync();
            }
            listaNota =
                ` Item de lista ${listKindLabel(listKind)}, nível ${r.levels[0] + 1}, marcador "${r.markers[0] || '•'}"` +
                    `${r.joined ? ' — continua a lista vizinha' : ''}.`;
        }
        else if (useHouse && !isHeadingStyleRef(ref)) {
            // Inserido logo depois de um item, o parágrafo herdava a lista.
            await ensureOutOfList(context, [para], [ref]);
        }
        const updatedFields = useHouse && isHeadingStyleRef(ref) ? await updateDynamicFields(context) : 0;
        const label = isStyleRef(input.styleRef) ? input.styleRef : (_b = input.style) !== null && _b !== void 0 ? _b : 'body_paragraph';
        const fieldsNote = updatedFields ? ` Campos atualizados: ${updatedFields}.` : '';
        const neighborhood = await resultNeighborhood(context, para);
        return `Parágrafo inserido (estilo ${label}, ${where}).${nota}${listaNota}${fieldsNote}${neighborhood}`;
    });
}
export async function replaceText(input) {
    return Word.run(async (context) => {
        const results = context.document.body.search(input.query, { matchCase: !!input.matchCase });
        results.load('text');
        await context.sync();
        if (results.items.length === 0)
            throw new Error(`Texto não encontrado: "${input.query}".`);
        const bodyFirst = await preferBodyRanges(context, results.items);
        // Seguro por padrão: só a 1ª ocorrência, salvo all=true (evita cascata —
        // substituir uma palavra comum no documento inteiro sem intenção).
        const targets = input.all === true ? bodyFirst : bodyFirst.slice(0, 1);
        const firstPara = targets[0].paragraphs.getFirst();
        // Trocar o trecho INTEIRO de um comentário apagava o comentário. Sem
        // comentário no documento, a troca em lote de sempre (1 sync).
        const comentarios = { kept: 0, recreated: 0, lost: 0 };
        if (await documentHasComments(context)) {
            for (const r of targets) {
                const o = await replaceKeepingComments(context, r, input.replacement);
                comentarios.kept += o.kept;
                comentarios.recreated += o.recreated;
                comentarios.lost += o.lost;
            }
        }
        else {
            targets.forEach((r) => r.insertText(input.replacement, Word.InsertLocation.replace));
            await context.sync();
        }
        const scope = input.all === true ? '' : ` (de ${results.items.length} encontradas)`;
        const neighborhood = await resultNeighborhood(context, firstPara);
        return (`Substituída(s) ${targets.length} ocorrência(s)${scope}.${commentOutcomeNote(comentarios)}` +
            neighborhood);
    });
}
/** Normaliza texto p/ comparar expectedPrefix (espaços colapsados, minúsculo). */
function normalizeForPrefix(s) {
    return (s || '').replace(/\s+/g, ' ').trim().toLowerCase();
}
export async function replaceParagraphs(input) {
    return Word.run(async (context) => {
        var _a, _b;
        const items = (_a = input.replacements) !== null && _a !== void 0 ? _a : [];
        if (!items.length)
            throw new Error('Nenhuma substituição informada (replacements vazio).');
        const paras = context.document.body.paragraphs;
        paras.load('items/text');
        await context.sync();
        const regions = markPrologueAsCover(await loadParagraphRegions(context, paras.items), await coverPrologueEnd(context, paras.items));
        // Substitui o TEXTO de cada parágrafo pelo Range (preserva o estilo do
        // parágrafo). Uma única chamada troca N parágrafos — é o que torna operações
        // em massa (traduzir/reescrever o documento inteiro) baratas: 1 leitura + 1
        // escrita, em vez de dezenas de round-trips parágrafo a parágrafo.
        //
        // `expectedPrefix` é o "optimistic locking" desta operação: os índices vêm
        // de um read_document_text que pode ter acontecido passos atrás — se algo
        // inseriu/removeu parágrafos no meio, os índices deslocam e a escrita cairia
        // no parágrafo ERRADO silenciosamente (o pior tipo de corrupção). Com o
        // prefixo, o handler confere que o alvo ainda é o texto que o modelo leu;
        // divergência = pula e reporta, para o modelo reler e reenviar.
        let count = 0;
        let outOfRange = 0;
        let skippedToc = 0;
        const staleDetails = [];
        // Parágrafo com comentário ancorado no meio: a troca preserva a thread (ver
        // replaceKeepingComments). Só ESSES parágrafos vão pelo caminho lento (2
        // idas ao Word cada): antes, um único comentário no documento fazia o lote
        // inteiro ir por ele — traduzir 200 parágrafos virava ~400 idas ao Word.
        const comComentario = await paragraphsWithComments(context, items.map((r) => paras.items[r.index]));
        const comentarios = { kept: 0, recreated: 0, lost: 0 };
        for (const [k, r] of items.entries()) {
            const p = paras.items[r.index];
            if (!p) {
                outOfRange++;
                continue;
            }
            if (regions[r.index] !== 'body') {
                skippedToc++;
                continue;
            }
            if (r.expectedPrefix) {
                const expected = normalizeForPrefix(r.expectedPrefix).slice(0, 60);
                const actual = normalizeForPrefix(p.text || '');
                if (expected && !actual.startsWith(expected)) {
                    if (staleDetails.length < 5) {
                        staleDetails.push(`[${r.index}] esperado "${briefText(r.expectedPrefix, 40)}" × atual "${briefText(p.text || '', 40)}"`);
                    }
                    else if (staleDetails.length === 5) {
                        staleDetails.push('…');
                    }
                    continue;
                }
            }
            // Um `text` = UM parágrafo: quebras de linha viram espaço para não
            // dividir o parágrafo (o que deslocaria a estrutura).
            const clean = String((_b = r.text) !== null && _b !== void 0 ? _b : '').replace(/\r?\n/g, ' ');
            if (comComentario[k]) {
                // 'Content' e não o parágrafo inteiro: a marca de parágrafo fica, senão
                // a troca por dentro apagaria a emenda com o parágrafo seguinte.
                const o = await replaceKeepingComments(context, p.getRange('Content'), clean);
                comentarios.kept += o.kept;
                comentarios.recreated += o.recreated;
                comentarios.lost += o.lost;
            }
            else {
                p.getRange().insertText(clean, Word.InsertLocation.replace);
            }
            count++;
        }
        await context.sync();
        const notes = [
            outOfRange ? `${outOfRange} índice(s) fora do range ignorado(s)` : '',
            skippedToc ? `${skippedToc} parágrafo(s) de região protegida preservado(s)` : '',
        ].filter(Boolean);
        const note = notes.length ? ` (${notes.join('; ')})` : '';
        const staleNote = staleDetails.length
            ? `\n⚠️ ${staleDetails.length > 5 ? '6+' : staleDetails.length} parágrafo(s) PULADO(S): o texto atual não confere ` +
                `com o expectedPrefix (o documento mudou desde a leitura dos índices): ` +
                `${staleDetails.join('; ')}. Releia com read_document_text e reenvie só os pendentes.`
            : '';
        return (`Substituídos ${count} parágrafo(s), preservando o estilo${note}.` +
            `${commentOutcomeNote(comentarios)}${staleNote}`);
    });
}
/**
 * Apaga os parágrafos que ficaram VAZIOS depois de uma remoção de texto.
 *
 * `Range.delete()` tira o texto, não a marca de parágrafo: apagar todo o
 * conteúdo de uma linha deixava a linha lá, vazia. Em documento editado por
 * vários turnos esses resíduos se somam e viram o "espaçamento errado" que o
 * usuário vê. Aqui a remoção fica completa na origem.
 *
 * Nunca toca em parágrafo ESTRUTURAL (imagem ancorada / quebra de página — ver
 * `isStructuralParagraph`), em parágrafo de tabela, no vazio exigido depois de
 * tabela, nem no último do corpo, que o template reserva. Best-effort: falha
 * aqui não invalida a remoção que já aconteceu.
 */
async function dropParagraphsEmptiedBy(context, paragraphs) {
    try {
        const candidatos = paragraphs.filter((p) => !p.isNullObject);
        if (!candidatos.length)
            return 0;
        candidatos.forEach((p) => p.load('text,tableNestingLevel'));
        // O vizinho de cima diz se o vazio é o EXIGIDO depois de uma tabela.
        const anteriores = candidatos.map((p) => {
            const ant = p.getPreviousOrNullObject();
            ant.load('isNullObject,tableNestingLevel');
            return ant;
        });
        const todos = context.document.body.paragraphs;
        todos.load('items/text');
        await context.sync();
        const ultimo = todos.items[todos.items.length - 1];
        const vazios = candidatos.filter((p, i) => {
            var _a, _b;
            return !(p.text || '').trim() &&
                ((_a = p.tableNestingLevel) !== null && _a !== void 0 ? _a : 0) === 0 &&
                p !== ultimo &&
                // apagar o texto que vinha logo depois de uma tabela deixava o vazio
                // exigido pela norma — e esta limpeza o levava junto
                !(!anteriores[i].isNullObject && ((_b = anteriores[i].tableNestingLevel) !== null && _b !== void 0 ? _b : 0) > 0);
        });
        if (!vazios.length)
            return 0;
        const ooxml = vazios.map((p) => p.getOoxml());
        await context.sync();
        const remover = vazios.filter((_, i) => !isStructuralParagraph(ooxml[i].value || ''));
        if (!remover.length)
            return 0;
        remover.forEach((p) => p.delete());
        await context.sync();
        return remover.length;
    }
    catch (e) {
        console.warn('[dropParagraphsEmptiedBy] ignorado (best-effort):', e);
        return 0;
    }
}
export async function deleteText(input) {
    return Word.run(async (context) => {
        const results = context.document.body.search(input.query, { matchCase: !!input.matchCase });
        results.load('text');
        await context.sync();
        if (results.items.length === 0)
            throw new Error(`Texto não encontrado: "${input.query}".`);
        const bodyFirst = await preferBodyRanges(context, results.items);
        // Seguro por padrão: só a 1ª ocorrência, salvo all=true.
        const targets = input.all === true ? bodyFirst : bodyFirst.slice(0, 1);
        const firstPara = targets[0].paragraphs.getFirst();
        // Guarda os parágrafos afetados ANTES de apagar: depois do delete não há
        // como voltar do range à linha que o continha.
        const afetados = targets.map((r) => r.paragraphs.getFirst());
        await assertOldCoverSheetKept(context, afetados, targets.map((r) => r.text || ''));
        let threads = 0;
        for (const r of targets)
            threads += (await commentsInside(context, r)).length;
        targets.forEach((r) => r.delete());
        await context.sync();
        const orfaos = await dropParagraphsEmptiedBy(context, afetados);
        const scope = input.all === true ? '' : ` (de ${results.items.length} encontradas)`;
        const linhas = orfaos ? ` ${orfaos} linha(s) que ficaram vazias foram removidas.` : '';
        // best-effort: se o parágrafo inteiro sumiu junto, o helper devolve ''.
        const neighborhood = await resultNeighborhood(context, firstPara);
        return `Removida(s) ${targets.length} ocorrência(s)${scope}.${linhas}${removedThreadsNote(threads)}${neighborhood}`;
    });
}
/** Teto de parágrafos por remoção — acima disso, o modelo divide o pedido. */
const DELETE_MAX_PARAGRAPHS = 600;
// ---------------------------------------------------------------------------
// Folha de rosto antiga sob proteção (revisão do "Formatar")
// ---------------------------------------------------------------------------
// Durante a revisão de uma seção com folha de rosto antiga, o painel liga este
// guarda com as linhas dela. Apagá-las só passa quando tudo o que dizem já está
// na capa — senão a ferramenta recusa e diz o que falta levar. Medido: o modelo
// real levou título e data, esqueceu o cliente e apagou a folha de rosto.
let oldCoverSheetLines = null;
/** Liga (linhas da folha de rosto) ou desliga (`null`) o guarda. */
export function setOldCoverSheetGuard(lines) {
    oldCoverSheetLines = lines && lines.length ? lines.map((l) => l.replace(/\f/g, '').trim()) : null;
}
const COVER_PLACEHOLDER = /^(título do documento|titulo do documento|nome da (área ou )?empresa|nome do cliente|_*)$/i;
/** Os campos da capa como estão agora (placeholder conta como vazio). */
async function readCoverFields(context) {
    const ccs = context.document.contentControls;
    ccs.load('items/tag,items/text');
    await context.sync();
    const campo = (tag) => {
        var _a;
        const t = (((_a = ccs.items.find((c) => c.tag === tag)) === null || _a === void 0 ? void 0 : _a.text) || '').replace(/\s+/g, ' ').trim();
        return COVER_PLACEHOLDER.test(t) ? '' : t;
    };
    return {
        title: campo('document_title'),
        client: campo('client_name'),
        date: campo('document_date'),
        documentType: campo('document_type'),
    };
}
/**
 * Recusa a remoção se ela tira da folha de rosto antiga algo que a capa ainda
 * não tem. `apagado[i]` é o texto que sai do parágrafo `paragrafos[i]` (o
 * parágrafo inteiro, ou só o trecho no delete_text).
 */
async function assertOldCoverSheetKept(context, paragrafos, apagado) {
    const guardadas = oldCoverSheetLines;
    if (!guardadas || !paragrafos.length)
        return;
    paragrafos.forEach((p) => p.load('text'));
    await context.sync();
    const saindo = paragrafos
        .map((p, i) => ({ linha: (p.text || '').replace(/\f/g, '').trim(), sai: apagado === null || apagado === void 0 ? void 0 : apagado[i] }))
        .filter((x) => x.linha && guardadas.includes(x.linha))
        .map((x) => { var _a; return (_a = x.sai) !== null && _a !== void 0 ? _a : x.linha; });
    if (!saindo.length)
        return;
    const capa = await readCoverFields(context);
    // Uma linha por vez, para citar a linha ORIGINAL: a sobra vem normalizada
    // ("xyz"), e o modelo precisa do texto como está para levá-lo à capa.
    const pendentes = saindo.filter((t) => coverSheetLeftovers([t], capa).length > 0);
    if (pendentes.length) {
        throw new Error(`Não apaguei a folha de rosto antiga: «${pendentes.join('», «')}» tem informação que ainda ` +
            'não está na capa. Leve para a capa com fill_content_control (client_name, se for o ' +
            'cliente; document_title, se for o título) e apague depois. Se não couber na capa, ' +
            'deixe a folha de rosto como está.');
    }
}
/**
 * Apaga um BLOCO de parágrafos — o conteúdo de uma seção, a seção inteira, ou
 * um intervalo por índice — levando junto o que está no meio: imagens, tabelas
 * e itens de lista.
 *
 * `delete_text` apaga TEXTO: pedir "apague o conteúdo do tópico 3" deixava para
 * trás as imagens e os bullets (que não são o texto buscado), e só funcionava
 * quando o usuário insistia em "apague tudo" (visto pelo QA). Aqui a unidade é o
 * parágrafo: o intervalo vira um Range só, do primeiro ao último parágrafo, e o
 * Word remove tudo o que está dentro. Medido no Word: tabela e imagem no meio
 * saem junto, sem sobrar linha vazia na emenda. Um intervalo que começa ou
 * termina DENTRO de uma tabela é estendido até a tabela inteira (um Range que
 * termina no meio de uma tabela é recusado pelo Word).
 */
export async function deleteParagraphs(input) {
    return Word.run(async (context) => {
        const map = await loadBodyMap(context);
        const total = map.items.length;
        let ini;
        let fim;
        let rotulo;
        if (input.section) {
            const titulo = await resolveAnchorParagraph(context, input.section);
            const idx = await indexOfParagraph(context, map, titulo);
            const nivel = idx >= 0 && !map.shapes[idx].inTable ? map.shapes[idx].level : null;
            if (idx < 0 || nivel === null || nivel < 1) {
                throw new Error(`"${briefText(input.section, 60)}" não é o título de uma seção. Passe o título EXATO ` +
                    'do tópico (como aparece no outline) ou use startIndex/endIndex de read_document_text.');
            }
            fim = sectionEndIndex(map.shapes, idx, nivel);
            ini = input.includeHeading ? idx : idx + 1;
            if (ini > fim) {
                return `A seção "${briefText(input.section, 60)}" não tem conteúdo abaixo do título — nada a apagar.`;
            }
            rotulo = input.includeHeading
                ? `a seção "${briefText(input.section, 60)}" inteira (título e conteúdo)`
                : `o conteúdo da seção "${briefText(input.section, 60)}" (o título ficou)`;
        }
        else if (typeof input.startIndex === 'number') {
            ini = input.startIndex;
            fim = typeof input.endIndex === 'number' ? input.endIndex : ini;
            if (ini < 0 || fim < ini || fim >= total) {
                throw new Error(`Intervalo inválido: ${ini}..${fim} (o corpo tem ${total} parágrafos, índices 0..${total - 1}).`);
            }
            if (input.expectedStartPrefix) {
                const esperado = normalizeForPrefix(input.expectedStartPrefix).slice(0, 60);
                const atual = normalizeForPrefix(map.items[ini].text || '');
                if (esperado && !atual.startsWith(esperado)) {
                    throw new Error(`O parágrafo ${ini} não começa mais com "${briefText(input.expectedStartPrefix, 40)}" ` +
                        `(agora: "${briefText(map.items[ini].text || '', 40)}"): o documento mudou desde a ` +
                        'leitura. Releia com read_document_text e use os índices novos.');
                }
            }
            rotulo = `os parágrafos ${ini}–${fim}`;
        }
        else {
            throw new Error('Informe `section` (o título da seção) OU `startIndex`/`endIndex` (índices de read_document_text).');
        }
        // Tabela cortada ao meio não existe: o intervalo cresce até a tabela inteira.
        while (ini > 0 && map.shapes[ini].inTable && map.shapes[ini - 1].inTable)
            ini--;
        while (fim < total - 1 && map.shapes[fim].inTable && map.shapes[fim + 1].inTable)
            fim++;
        if (fim - ini + 1 > DELETE_MAX_PARAGRAPHS) {
            throw new Error(`São ${fim - ini + 1} parágrafos — acima do teto de ${DELETE_MAX_PARAGRAPHS} por remoção. ` +
                'Apague por partes (uma seção ou um intervalo menor por vez).');
        }
        // Capa e sumário são regiões protegidas: nem um parágrafo delas entra.
        const regioes = markPrologueAsCover(await loadParagraphRegions(context, map.items.slice(ini, fim + 1)), await coverPrologueEnd(context, map.items), ini);
        const protegida = regioes.findIndex((r) => r !== 'body');
        if (protegida >= 0) {
            throw new Error(`Remoção bloqueada: o parágrafo ${ini + protegida} está em região protegida. ` +
                protectedRegionMessage(regioes[protegida]));
        }
        const inicio = map.shapes[ini].inTable
            ? map.items[ini].parentTable.getRange('Whole')
            : map.items[ini].getRange('Whole');
        const termino = map.shapes[fim].inTable
            ? map.items[fim].parentTable.getRange('Whole')
            : map.items[fim].getRange('Whole');
        const alvo = inicio.expandTo(termino);
        const tabelas = alvo.tables;
        tabelas.load('items');
        const imagens = alvo.inlinePictures;
        imagens.load('items');
        const vizinho = ini > 0 ? map.items[ini - 1] : null;
        await context.sync();
        const nTabelas = tabelas.items.length;
        const nImagens = imagens.items.length;
        // Threads ancoradas no bloco somem com ele — o retorno tem que dizer, senão
        // o modelo relata "apaguei o tópico" e o usuário descobre depois que a
        // revisão do colega foi junto.
        const nThreads = (await commentsInside(context, alvo)).length;
        await assertOldCoverSheetKept(context, map.items.slice(ini, fim + 1));
        alvo.delete();
        await context.sync();
        const extras = [
            nTabelas ? `${nTabelas} tabela(s)` : '',
            nImagens ? `${nImagens} imagem(ns)` : '',
            nThreads ? `${nThreads} thread(s) de comentário` : '',
        ].filter(Boolean);
        const neighborhood = vizinho ? await resultNeighborhood(context, vizinho) : '';
        return (`Removido ${rotulo}: ${fim - ini + 1} parágrafo(s)` +
            `${extras.length ? `, incluindo ${extras.join(' e ')}` : ''}.` +
            ' Os índices depois deste ponto mudaram — releia antes de usar índices antigos.' +
            neighborhood);
    });
}
export async function moveText(input) {
    return Word.run(async (context) => {
        var _a;
        const results = context.document.body.search(input.query, { matchCase: !!input.matchCase });
        results.load('text');
        await context.sync();
        if (results.items.length === 0)
            throw new Error(`Texto não encontrado: "${input.query}".`);
        const bodyFirst = await preferBodyRanges(context, results.items);
        const loc = (_a = input.location) !== null && _a !== void 0 ? _a : 'endOfDocument';
        await assertEditableLocation(context, loc, 'Destino de movimentação');
        // Captura o texto da 1ª ocorrência, remove da origem e reinsere no destino.
        const src = bodyFirst[0];
        const moved = src.text;
        // Threads inteiras dentro do trecho morriam com a origem (medido): ficam
        // fotografadas para serem recriadas no texto que chega ao destino.
        const fotos = await photographComments(context, await commentsInside(context, src));
        // Mesma armadilha do deleteText: tirar o texto deixa a linha vazia na
        // ORIGEM. Num "mova o parágrafo X para depois de Y" isso rendia um buraco
        // exatamente onde o conteúdo estava.
        const origem = src.paragraphs.getFirst();
        src.delete();
        await context.sync();
        await dropParagraphsEmptiedBy(context, [origem]);
        const body = context.document.body;
        let destPara = null;
        let destino;
        if (loc === 'endOfDocument') {
            const placement = await resolveEndOfDocumentInsertion(context);
            if (placement.reuse) {
                destino = placement.reuse.insertText(moved, Word.InsertLocation.end);
                destPara = placement.reuse;
            }
            else {
                destPara = body.insertParagraph(moved, Word.InsertLocation.end);
                if (placement.breakAfterToc)
                    destPara.insertBreak(Word.BreakType.page, Word.InsertLocation.before);
                destino = destPara.getRange('Content');
            }
        }
        else if (loc === 'startOfDocument') {
            const start = await resolveStartOfBodyContent(context);
            destPara = start
                ? insertParagraphAtBodyStart(start, moved)
                : body.insertParagraph(moved, Word.InsertLocation.start);
            destino = destPara.getRange('Content');
        }
        else if (loc === 'afterSelection')
            destino = context.document.getSelection().insertText(moved, Word.InsertLocation.after);
        else
            destino = context.document.getSelection().insertText(moved, Word.InsertLocation.replace);
        await context.sync();
        const movidos = fotos.length ? await recreateComments(context, fotos, destino) : SEM_COMENTARIOS;
        const neighborhood = destPara ? await resultNeighborhood(context, destPara) : '';
        return `Texto movido (${loc}).${commentOutcomeNote(movidos)}${neighborhood}`;
    });
}
export async function deleteTable(input) {
    return Word.run(async (context) => {
        const tables = context.document.body.tables;
        tables.load('items');
        await context.sync();
        if (tables.items.length === 0)
            throw new Error('Não há nenhuma tabela no documento.');
        // Carrega os valores das células para identificar quais estão vazias.
        // (Tabela vazia não tem texto, então delete_text nunca a encontra — por isso
        //  esta ferramenta age sobre a ESTRUTURA da tabela, não sobre texto.)
        tables.items.forEach((t) => t.load('values'));
        await context.sync();
        const isEmpty = (t) => { var _a; return ((_a = t.values) !== null && _a !== void 0 ? _a : []).every((row) => row.every((cell) => !cell || !String(cell).trim())); };
        let targets;
        let how;
        if (typeof input.index === 'number') {
            const t = tables.items[input.index];
            if (!t)
                throw new Error(`Tabela de índice ${input.index} não existe (há ${tables.items.length}).`);
            targets = [t];
            how = `índice ${input.index}`;
        }
        else if (input.all) {
            targets = tables.items;
            how = 'todas';
        }
        else {
            // Padrão: remove as tabelas VAZIAS (caso de uso mais comum).
            targets = tables.items.filter(isEmpty);
            if (targets.length === 0)
                throw new Error('Nenhuma tabela vazia encontrada. Para apagar uma tabela com conteúdo, ' +
                    'passe `index` (a posição dela, começando em 0) ou `all: true`.');
            how = 'vazias';
        }
        targets.forEach((t) => t.delete());
        await context.sync();
        return `Removida(s) ${targets.length} tabela(s) (${how}).`;
    });
}
/**
 * Um parágrafo que só desenha o traço da capa (uma linha de "_"), não um valor.
 * Cada caixa de texto da capa Norven tem DOIS parágrafos: o traço e o campo.
 * Vive aqui (e não em `template.ts`) porque template.ts importa tools.ts — o
 * contrário criaria ciclo.
 */
export function isCoverRuleParagraph(text) {
    const t = (text !== null && text !== void 0 ? text : '').trim();
    return t.length > 0 && /^_+$/.test(t);
}
export async function fillContentControl(input) {
    return Word.run(async (context) => {
        const ccs = context.document.contentControls;
        ccs.load('tag,title');
        await context.sync();
        const target = ccs.items.find((c) => (input.tag && c.tag === input.tag) || (input.title && c.title === input.title));
        if (!target)
            throw new Error('Content control não encontrada (tag/title).');
        // `insertText(replace)` troca TODO o conteúdo da control. Nos documentos
        // criados antes da correção em `tagCoverShapes`, a control envolve a caixa
        // de texto inteira — traço da capa incluído —, então preencher o campo
        // apagava o traço junto. Havendo um parágrafo de traço lá dentro, escreve
        // só no parágrafo do valor.
        target.paragraphs.load('items/text');
        await context.sync();
        const paras = target.paragraphs.items;
        const traco = paras.filter((p) => isCoverRuleParagraph(p.text));
        if (traco.length && paras.length > 1) {
            const valor = paras.filter((p) => !isCoverRuleParagraph(p.text)).pop();
            if (valor) {
                valor.insertText(input.text, Word.InsertLocation.replace);
            }
            else {
                // Só traço na control (campo apagado à mão): abre a linha do valor
                // abaixo dele em vez de escrever por cima.
                traco[traco.length - 1].insertParagraph(input.text, Word.InsertLocation.after);
            }
        }
        else {
            target.insertText(input.text, Word.InsertLocation.replace);
        }
        await context.sync();
        return 'Marcação preenchida.';
    });
}
// ======================================================================
// Escrita: estrutura (tabelas e listas)
// ======================================================================
export async function insertTable(input) {
    return Word.run(async (context) => {
        var _a, _b;
        const headers = input.headers;
        const rows = (_a = input.rows) !== null && _a !== void 0 ? _a : [];
        if (!rows.length && !(headers && headers.length))
            throw new Error('Tabela sem dados.');
        const values = headers && headers.length ? [headers, ...rows] : rows;
        const rowCount = values.length;
        const colCount = Math.max(...values.map((r) => r.length));
        // Normaliza para retângulo (preenche células faltantes com '').
        const norm = values.map((r) => {
            const a = [...r];
            while (a.length < colCount)
                a.push('');
            return a;
        });
        const loc = (_b = input.location) !== null && _b !== void 0 ? _b : 'endOfDocument';
        const body = context.document.body;
        let table;
        let where;
        let nota = '';
        if (input.anchorText) {
            // Posicionamento ANCORADO: a tabela entra ANTES de um parágrafo-hospedeiro
            // vazio posto no lugar pedido — e o hospedeiro vira o vazio que a norma
            // exige depois dela. Antes a tabela colava no parágrafo seguinte.
            const spot = await resolveAnchoredSpot(context, input.anchorText, input.anchorPosition);
            const hospedeiro = spot.make('');
            applyHouseParagraphStyle(hospedeiro, 'body_paragraph');
            table = hospedeiro.insertTable(rowCount, colCount, Word.InsertLocation.before, norm);
            where = spot.where;
            nota = spot.note;
        }
        else if (loc === 'startOfDocument') {
            await assertEditableLocation(context, loc, 'Inserção de tabela');
            const start = await resolveStartOfBodyContent(context);
            if (start) {
                table = start.anchor
                    .getRange()
                    .insertTable(rowCount, colCount, bodyStartInsertLocation(start.mode), norm);
                where = bodyStartWhere(start.skipped);
            }
            else {
                table = body.insertTable(rowCount, colCount, Word.InsertLocation.start, norm);
                where = loc;
            }
        }
        else if (loc === 'afterSelection' || loc === 'replaceSelection') {
            await assertEditableLocation(context, loc, 'Inserção de tabela');
            table = context.document
                .getSelection()
                .insertTable(rowCount, colCount, Word.InsertLocation.after, norm);
            where = loc;
        }
        else {
            const placement = await resolveEndOfDocumentInsertion(context);
            if (placement.breakAfterToc) {
                // Documento terminava dentro do Sumário: tabela começa em página própria.
                const pageStart = body.insertParagraph('', Word.InsertLocation.end);
                pageStart.insertBreak(Word.BreakType.page, Word.InsertLocation.before);
                table = pageStart.getRange().insertTable(rowCount, colCount, Word.InsertLocation.after, norm);
                where = 'endOfDocument (nova página aberta após o Sumário)';
            }
            else {
                table = body.insertTable(rowCount, colCount, Word.InsertLocation.end, norm);
                where = loc;
            }
        }
        // Estilo de tabela da marca: Muli no corpo, cabeçalho roxo c/ texto branco.
        // (Também normaliza o estilo de parágrafo das células — ver houseStyle.)
        const hasHeader = !!(headers && headers.length);
        applyHouseTableStyle(table, hasHeader, colCount, rowCount);
        await context.sync();
        // Início do corpo/seleção: a tabela entra antes de um parágrafo com texto.
        await ensureBlankAfterTable(context, table);
        return `Tabela ${rowCount}x${colCount} inserida (estilo Norven, ${where}).${nota}`;
    });
}
export async function insertList(input) {
    return Word.run(async (context) => {
        var _a, _b;
        // Item = texto (nível 1) ou { text, level } — subitem é level 2.
        const itens = parseListItems(input.items);
        if (!itens.length)
            throw new Error('Lista sem itens.');
        const kind = insertListKind(input.ordered, itens.map((i) => i.text));
        // O marcador é da lista: um "/" ou "1." digitado no item sairia dobrado.
        const digitados = itens.filter((i) => typedListMarker(i.text)).length;
        // Pontuação da norma (';' nos intermediários, '.' no último): só a que falta.
        const niveisPedidos = normalizeLevels(itens.map((i) => i.level));
        const pontuados = punctuateListItems(itens.map((i, k) => ({ text: stripTypedListMarker(i.text), level: niveisPedidos[k] })));
        const textos = pontuados.items.map((i) => i.text);
        const loc = (_a = input.location) !== null && _a !== void 0 ? _a : 'endOfDocument';
        const body = context.document.body;
        // Insere o PRIMEIRO parágrafo no ponto certo (âncora tem prioridade sobre
        // location). Os demais são encadeados APÓS o anterior — corrige o bug em que
        // itens 2..n iam para o fim do documento quando location != endOfDocument.
        const spot = input.anchorText
            ? await resolveAnchoredSpot(context, input.anchorText, input.anchorPosition)
            : null;
        if (!spot)
            await assertEditableLocation(context, loc, 'Inserção de lista');
        const placement = !spot && loc === 'endOfDocument'
            ? await resolveEndOfDocumentInsertion(context)
            : { breakAfterToc: false };
        // `insertFirst` é síncrono: o início do corpo precisa estar resolvido antes.
        const startPlacement = !spot && loc === 'startOfDocument' ? await resolveStartOfBodyContent(context) : null;
        const insertFirst = (text) => {
            if (spot)
                return spot.make(text);
            if (loc === 'startOfDocument') {
                return startPlacement
                    ? insertParagraphAtBodyStart(startPlacement, text)
                    : body.insertParagraph(text, Word.InsertLocation.start);
            }
            if (loc === 'afterSelection' || loc === 'replaceSelection')
                return context.document.getSelection().insertParagraph(text, Word.InsertLocation.after);
            if (placement.reuse) {
                // Reaproveita o parágrafo vazio final (evita linha em branco antes da lista).
                placement.reuse.insertText(text, Word.InsertLocation.end);
                return placement.reuse;
            }
            const p = body.insertParagraph(text, Word.InsertLocation.end);
            // Documento terminava dentro do Sumário: lista começa em página própria.
            if (placement.breakAfterToc)
                p.insertBreak(Word.BreakType.page, Word.InsertLocation.before);
            return p;
        };
        const where = spot ? spot.where : loc;
        // O TEXTO entra primeiro, num sync só; a lista é montada depois, sobre
        // parágrafos que já existem. Se montar a lista falhar, o conteúdo não se
        // perde nem se duplica num retry (o retorno de erro avisa o modelo).
        const paras = [];
        let prev = insertFirst(textos[0]);
        paras.push(prev);
        for (let i = 1; i < textos.length; i++) {
            prev = prev.insertParagraph(textos[i], Word.InsertLocation.after);
            paras.push(prev);
        }
        await context.sync();
        // Lista nativa da marca, nível a nível. Encostada numa lista do mesmo tipo
        // (ex.: dois passos depois do passo 3), continua nela.
        const r = await makeHouseList(context, paras, kind, itens.map((i) => i.level), {
            joinAdjacent: true,
        });
        const niveis = new Set(r.levels).size > 1 ? `, ${Math.max(...r.levels) + 1} níveis` : '';
        const notas = [
            r.joined
                ? ` Continua a lista ${r.joined === 'previous' ? 'de cima' : 'de baixo'} (mesmo tipo, encostada), em vez de abrir outra.`
                : '',
            digitados
                ? ` ${digitados} marcador(es) digitado(s) no texto dos itens foram retirados — o marcador é o da lista.`
                : '',
            pontuados.added
                ? ` Pontuação da norma acrescentada em ${pontuados.added} item(ns) que terminavam sem ` +
                    "pontuação (';' nos intermediários, '.' no último)."
                : '',
            ` Marcadores: ${markersSummary(r.markers)}.`,
        ].join('');
        return (`Lista ${listKindLabel(kind)} de ${paras.length} itens inserida (estilo Norven${niveis}, ${where}).` +
            `${(_b = spot === null || spot === void 0 ? void 0 : spot.note) !== null && _b !== void 0 ? _b : ''}${notas}`);
    });
}
// ======================================================================
// Escrita: formatação
// ======================================================================
export async function applyFormatting(input) {
    return Word.run(async (context) => {
        var _a, _b;
        const target = (_a = input.target) !== null && _a !== void 0 ? _a : (input.query ? 'firstMatch' : 'selection');
        const patch = (_b = input.font) !== null && _b !== void 0 ? _b : {};
        let ranges;
        if (target === 'selection') {
            const sel = context.document.getSelection();
            sel.load('text');
            await context.sync();
            if (!sel.text.trim())
                throw new Error('Nada selecionado para formatar.');
            await assertEditableParagraph(context, sel.paragraphs.getFirst(), 'Formatação');
            ranges = [sel];
        }
        else {
            if (!input.query)
                throw new Error('query é obrigatório para firstMatch/allMatches.');
            const results = context.document.body.search(input.query, { matchCase: !!input.matchCase });
            results.load('text');
            await context.sync();
            if (results.items.length === 0)
                throw new Error(`Texto não encontrado: "${input.query}".`);
            const bodyFirst = await preferBodyRanges(context, results.items);
            ranges = target === 'firstMatch' ? [bodyFirst[0]] : bodyFirst;
        }
        ranges.forEach((r) => applyFontPatch(r.font, patch));
        await context.sync();
        return `Formatação aplicada a ${ranges.length} trecho(s).`;
    });
}
export async function applyParagraphStyle(input) {
    return Word.run(async (context) => {
        var _a;
        let para;
        if (input.query) {
            const results = context.document.body.search(input.query, {});
            results.load('text');
            await context.sync();
            if (results.items.length === 0)
                throw new Error(`Texto não encontrado: "${input.query}".`);
            const bodyFirst = await preferBodyRanges(context, results.items);
            para = bodyFirst[0].paragraphs.getFirst();
        }
        else {
            para = context.document.getSelection().paragraphs.getFirst();
            await assertEditableParagraph(context, para, 'Aplicação de estilo');
        }
        const listKind = isStyleRef(input.styleRef) ? listKindOfRef(input.styleRef) : null;
        if (listKind) {
            // Vira item de lista NATIVA: o marcador digitado sai do texto, e o item
            // entra na lista vizinha do mesmo tipo (se encostar numa) ou abre uma.
            const marcadores = await removeTypedMarkers(context, [para]);
            const r = await makeHouseList(context, [para], listKind, [levelIndexFromInput(input.level)], {
                joinAdjacent: true,
            });
            if (input.alignment) {
                para.alignment = mapAlignment(input.alignment);
                await context.sync();
            }
            const neighborhood = await resultNeighborhood(context, para);
            return (`Estilo de parágrafo aplicado (${input.styleRef}): item de lista ${listKindLabel(listKind)}, ` +
                `nível ${r.levels[0] + 1}, marcador "${r.markers[0] || '•'}"` +
                `${r.joined ? ' — continua a lista vizinha' : ''}.` +
                `${marcadores ? ' Marcador digitado retirado do texto.' : ''}` +
                ' Para converter uma lista INTEIRA (ou vários parágrafos), use apply_paragraph_styles com os índices.' +
                neighborhood);
        }
        if (isStyleRef(input.styleRef)) {
            const ref = input.styleRef;
            // heading_3 é caixa alta: normaliza o texto existente antes de estilizar.
            if (needsUpper(ref)) {
                para.load('text');
                await context.sync();
                para.insertText(styleText(para.text.replace(/\s+$/, ''), ref), Word.InsertLocation.replace);
            }
            await restyleExisting(context, [para], [ref]);
        }
        else if (input.style) {
            para.styleBuiltIn = mapBuiltInStyle(input.style);
        }
        if (input.alignment)
            para.alignment = mapAlignment(input.alignment);
        await context.sync();
        // Item de lista que virou corpo/citação: fora da lista, sem recuo residual.
        if (isStyleRef(input.styleRef) && !isHeadingStyleRef(input.styleRef)) {
            await ensureOutOfList(context, [para], [input.styleRef]);
        }
        const updatedFields = isStyleRef(input.styleRef) && isHeadingStyleRef(input.styleRef)
            ? await updateDynamicFields(context)
            : 0;
        const label = isStyleRef(input.styleRef) ? input.styleRef : (_a = input.style) !== null && _a !== void 0 ? _a : '(alinhamento)';
        const fieldsNote = updatedFields ? ` Campos atualizados: ${updatedFields}.` : '';
        const neighborhood = await resultNeighborhood(context, para);
        return `Estilo de parágrafo aplicado (${label}).${fieldsNote}${neighborhood}`;
    });
}
/** Teto de parágrafos por chamada de apply_paragraph_styles (uma seção grande cabe). */
const STYLE_BATCH_MAX = 400;
/**
 * Estilos da marca em VÁRIOS parágrafos existentes de uma vez, por índice de
 * `read_document_text` — o texto não muda. É o caminho para formatar um
 * documento (ou uma seção) e para mexer em listas inteiras:
 *
 *  - índices consecutivos com `slash_bullet_list`/`numbered_list` do mesmo
 *    tipo viram UMA lista nativa, cada item no seu `level` (sem `level`, quem
 *    já era item mantém o nível — a hierarquia não achata);
 *  - trocar bullets por numeração (ou o contrário) é mandar os índices dos
 *    itens com o outro styleRef;
 *  - o marcador digitado ("/", "-", "1.") sai do texto do item;
 *  - capa, sumário e células de tabela ficam de fora (reportados), e o
 *    `expectedPrefix` protege contra índice defasado, como no replace_paragraphs.
 */
export async function applyParagraphStyles(input) {
    return Word.run(async (context) => {
        const pedidos = Array.isArray(input.items) ? input.items : [];
        if (!pedidos.length)
            throw new Error('Nenhum parágrafo informado (items vazio).');
        if (pedidos.length > STYLE_BATCH_MAX) {
            throw new Error(`São ${pedidos.length} parágrafos — acima do teto de ${STYLE_BATCH_MAX} por chamada. ` +
                'Formate por seção (um lote por seção).');
        }
        const map = await loadBodyMap(context);
        const total = map.items.length;
        const regioes = markPrologueAsCover(await loadParagraphRegions(context, map.items), await coverPrologueEnd(context, map.items));
        const pulados = { fora: 0, protegidos: 0, tabela: 0, estiloInvalido: 0, repetidos: 0 };
        const defasados = [];
        const vistos = new Set();
        const validos = [];
        for (const it of pedidos) {
            const idx = Number(it === null || it === void 0 ? void 0 : it.index);
            if (!Number.isInteger(idx) || idx < 0 || idx >= total) {
                pulados.fora++;
                continue;
            }
            if (!isStyleRef(it.styleRef)) {
                pulados.estiloInvalido++;
                continue;
            }
            if (vistos.has(idx)) {
                pulados.repetidos++;
                continue;
            }
            if (regioes[idx] !== 'body') {
                pulados.protegidos++;
                continue;
            }
            if (map.shapes[idx].inTable) {
                pulados.tabela++;
                continue;
            }
            if (it.expectedPrefix) {
                const esperado = normalizeForPrefix(it.expectedPrefix).slice(0, 60);
                const atual = normalizeForPrefix(map.items[idx].text || '');
                if (esperado && !atual.startsWith(esperado)) {
                    if (defasados.length < 5) {
                        defasados.push(`[${idx}] esperado "${briefText(it.expectedPrefix, 40)}" × atual "${briefText(map.items[idx].text || '', 40)}"`);
                    }
                    else if (defasados.length === 5) {
                        defasados.push('…');
                    }
                    continue;
                }
            }
            vistos.add(idx);
            const level = it.level !== undefined && it.level !== null ? levelIndexFromInput(it.level) : null;
            validos.push({ index: idx, ref: it.styleRef, level });
        }
        // (1) Estilos que não são de lista: títulos, corpo, citações — com a
        // ênfase do texto (negrito de um termo, itálico) preservada.
        const simples = validos.filter((v) => !listKindOfRef(v.ref));
        simples
            .filter((v) => needsUpper(v.ref))
            .forEach((v) => {
            const p = map.items[v.index];
            p.insertText(styleText((p.text || '').replace(/\s+$/, ''), v.ref), Word.InsertLocation.replace);
        });
        const semEnfase = await restyleExisting(context, simples.map((v) => map.items[v.index]), simples.map((v) => v.ref));
        const semTitulo = simples.filter((v) => !isHeadingStyleRef(v.ref));
        const tirados = await ensureOutOfList(context, semTitulo.map((v) => map.items[v.index]), semTitulo.map((v) => v.ref));
        // (2) Listas: cada sequência de índices consecutivos do mesmo tipo é UMA
        // lista. Sem `level` no pedido, quem já era item mantém o nível — é o que
        // impede a formatação de achatar a hierarquia.
        const itensDeLista = validos.filter((v) => listKindOfRef(v.ref));
        const estados = await readListStates(context, itensDeLista.map((v) => map.items[v.index]));
        const grupos = groupListRuns(itensDeLista.map((v, k) => {
            var _a;
            return ({
                index: v.index,
                kind: listKindOfRef(v.ref),
                level: (_a = v.level) !== null && _a !== void 0 ? _a : (estados[k].inList ? estados[k].level : 0),
            });
        }));
        let marcadores = 0;
        const listas = [];
        for (const g of grupos) {
            const paras = g.map((v) => map.items[v.index]);
            marcadores += await removeTypedMarkers(context, paras);
            // Sem juntar com a vizinha: o lote diz quais parágrafos são a lista, e
            // fundir com a lista encostada renumeraria o que não foi pedido.
            const r = await makeHouseList(context, paras, g[0].kind, g.map((v) => v.level));
            const faixa = g.length > 1 ? `${g[0].index}–${g[g.length - 1].index}` : `${g[0].index}`;
            listas.push(`[${faixa}] ${listKindLabel(g[0].kind)}: ${markersSummary(r.markers, 8)}` +
                `${r.joined ? ' (continua a lista vizinha)' : ''}`);
        }
        const updatedFields = simples.some((v) => isHeadingStyleRef(v.ref))
            ? await updateDynamicFields(context)
            : 0;
        const porEstilo = new Map();
        validos.forEach((v) => { var _a; return porEstilo.set(v.ref, ((_a = porEstilo.get(v.ref)) !== null && _a !== void 0 ? _a : 0) + 1); });
        const resumo = [...porEstilo.entries()].map(([ref, n]) => `${ref} ×${n}`).join(', ');
        const avisos = [
            pulados.fora ? `${pulados.fora} índice(s) fora do documento` : '',
            pulados.estiloInvalido ? `${pulados.estiloInvalido} styleRef inválido(s)` : '',
            pulados.repetidos ? `${pulados.repetidos} índice(s) repetido(s)` : '',
            pulados.protegidos ? `${pulados.protegidos} parágrafo(s) de capa/sumário preservado(s)` : '',
            pulados.tabela
                ? `${pulados.tabela} parágrafo(s) de tabela ignorado(s) (a tabela tem estilo próprio)`
                : '',
        ].filter(Boolean);
        return [
            `Estilos aplicados a ${validos.length} parágrafo(s)${resumo ? `: ${resumo}` : ''}.`,
            listas.length ? `Listas: ${listas.join('; ')}.` : '',
            marcadores
                ? `${marcadores} marcador(es) digitado(s) retirado(s) do texto — o marcador é o da lista.`
                : '',
            tirados ? `${tirados} parágrafo(s) saíram de uma lista.` : '',
            semEnfase
                ? `${semEnfase} trecho(s) com ênfase só em parte de uma palavra podem ter perdido o negrito/itálico — confira.`
                : '',
            updatedFields ? `Campos atualizados: ${updatedFields}.` : '',
            avisos.length ? `Pulados: ${avisos.join('; ')}.` : '',
            defasados.length
                ? `⚠️ ${defasados.length > 5 ? '6+' : defasados.length} parágrafo(s) PULADO(S): o texto atual não ` +
                    `confere com o expectedPrefix (o documento mudou desde a leitura): ${defasados.join('; ')}. ` +
                    'Releia e reenvie só os pendentes.'
                : '',
        ]
            .filter(Boolean)
            .join(' ');
    });
}
// ======================================================================
// Imagens (como OBJETOS do documento — não o conteúdo visual)
// ======================================================================
// O Word tem dois modelos de imagem, e confundi-los é a fonte clássica de
// bugs "não achei a imagem":
//  - InlinePicture (body.inlinePictures): embutida no fluxo do texto, como um
//    caractere. Redimensiona por width/height; NÃO tem posição X/Y — "mover"
//    significa realocar no texto (recriar junto a uma âncora).
//  - Shape tipo 'Picture' (body.shapes): flutuante, com left/top próprios.
//    Requer WordApiDesktop 1.2; em hosts sem suporte degradamos com mensagem
//    clara em vez de fingir que não há imagens.
// `list_images` unifica os dois com ids "inline-N" (posição na enumeração
// atual) e "float-{id}" (id REAL do Shape, estável). As demais ferramentas
// resolvem o id na hora — se o documento mudou, o erro instrui a relistar.
const PT_PER_CM = 28.35;
function ptToCm(pt) {
    return (pt / PT_PER_CM).toFixed(1);
}
function fmtSize(w, h) {
    return `${Math.round(w)}×${Math.round(h)}pt (~${ptToCm(w)}×${ptToCm(h)}cm)`;
}
/** O host suporta shapes flutuantes? (WordApiDesktop 1.2 — Word desktop.) */
function floatingShapesSupported() {
    try {
        return Office.context.requirements.isSetSupported('WordApiDesktop', '1.2');
    }
    catch (_a) {
        return false;
    }
}
/** Conta imagens flutuantes (0 se o host não suportar shapes). */
async function countFloatingPictures(context) {
    if (!floatingShapesSupported())
        return 0;
    try {
        const shapes = context.document.body.shapes;
        shapes.load('items/type');
        await context.sync();
        return shapes.items.filter((s) => String(s.type) === 'Picture').length;
    }
    catch (_a) {
        return 0;
    }
}
/** Carrega as imagens flutuantes (Shape tipo Picture) com propriedades. */
async function loadFloatingPictures(context) {
    if (!floatingShapesSupported())
        return [];
    const shapes = context.document.body.shapes;
    shapes.load('items/type');
    await context.sync();
    const pictures = shapes.items.filter((s) => String(s.type) === 'Picture');
    for (const s of pictures) {
        s.load('id,width,height,top,left,altTextDescription,relativeHorizontalPosition,relativeVerticalPosition,lockAspectRatio');
    }
    if (pictures.length)
        await context.sync();
    return pictures;
}
/**
 * Resolve um imageId ("inline-N" | "float-ID") nos objetos vivos do contexto.
 * Erros são ACIONÁVEIS: dizem ao modelo para rodar list_images de novo.
 */
async function resolveImage(context, imageId) {
    const inlineMatch = /^inline-(\d+)$/.exec(imageId !== null && imageId !== void 0 ? imageId : '');
    const floatMatch = /^float-(\d+)$/.exec(imageId !== null && imageId !== void 0 ? imageId : '');
    if (!inlineMatch && !floatMatch) {
        throw new Error(`imageId inválido: "${imageId}". Use o id devolvido por list_images (ex.: "inline-1", "float-257").`);
    }
    if (inlineMatch) {
        const idx = parseInt(inlineMatch[1], 10) - 1;
        const pics = context.document.body.inlinePictures;
        pics.load('items');
        await context.sync();
        if (idx < 0 || idx >= pics.items.length) {
            throw new Error(`Imagem "${imageId}" não existe — o documento tem ${pics.items.length} imagem(ns) ` +
                'inline agora. Rode list_images de novo (os ids inline-N são recalculados).');
        }
        return { kind: 'inline', pic: pics.items[idx] };
    }
    if (!floatingShapesSupported()) {
        throw new Error('Este host do Word não suporta manipular imagens flutuantes (requer Word desktop). ' +
            'Informe isso ao usuário.');
    }
    const wantedId = parseInt(floatMatch[1], 10);
    const shapes = context.document.body.shapes;
    shapes.load('items/type,items/id');
    await context.sync();
    const shape = shapes.items.find((s) => s.id === wantedId && String(s.type) === 'Picture');
    if (!shape) {
        throw new Error(`Imagem flutuante "${imageId}" não encontrada (pode ter sido removida). ` +
            'Rode list_images de novo.');
    }
    return { kind: 'float', shape };
}
export async function listImages() {
    return Word.run(async (context) => {
        // --- inline: propriedades + contexto de localização -------------------
        const pics = context.document.body.inlinePictures;
        pics.load('items');
        const paragraphs = context.document.body.paragraphs;
        paragraphs.load('items/text,items/style,items/styleBuiltIn');
        await context.sync();
        for (const p of pics.items) {
            p.load('width,height,altTextDescription');
        }
        if (pics.items.length)
            await context.sync();
        // Contexto de localização: mapeia cada imagem inline ao SEU parágrafo via
        // uniqueLocalId (WordApi 1.6) — exato, sem casar texto (que falharia em
        // parágrafos duplicados/vazios). Em host antigo, degrada sem seção.
        const MAX_PARAS_FOR_CONTEXT = 800;
        const contextParas = paragraphs.items.slice(0, MAX_PARAS_FOR_CONTEXT);
        const picParagraphIndex = [];
        try {
            contextParas.forEach((p) => p.load('uniqueLocalId'));
            pics.items.forEach((p) => p.paragraph.load('uniqueLocalId'));
            if (pics.items.length)
                await context.sync();
            const idToIndex = new Map();
            contextParas.forEach((p, i) => {
                if (p.uniqueLocalId)
                    idToIndex.set(p.uniqueLocalId, i);
            });
            pics.items.forEach((p, i) => {
                picParagraphIndex[i] = idToIndex.get(p.paragraph.uniqueLocalId);
            });
        }
        catch (_a) {
            /* host sem uniqueLocalId: lista sem contexto de seção */
        }
        // heading anterior mais próximo de cada parágrafo
        const lastHeadingUpTo = [];
        let lastHeading = null;
        contextParas.forEach((p, i) => {
            const lvl = headingLevel(p.style || '', String(p.styleBuiltIn || ''));
            if (lvl !== null && (p.text || '').trim())
                lastHeading = (p.text || '').trim();
            lastHeadingUpTo[i] = lastHeading;
        });
        // --- flutuantes --------------------------------------------------------
        const floating = await loadFloatingPictures(context);
        // --- monta o texto p/ o modelo ----------------------------------------
        const lines = [];
        lines.push(`${pics.items.length} imagem(ns) inline (embutidas no texto) e ` +
            `${floating.length} flutuante(s).`);
        pics.items.forEach((p, i) => {
            const paraIdx = picParagraphIndex[i];
            const section = paraIdx !== undefined ? lastHeadingUpTo[paraIdx] : null;
            const paraText = paraIdx !== undefined ? (contextParas[paraIdx].text || '').trim() : '';
            const where = section
                ? `seção "${section}"`
                : 'antes da primeira seção';
            const paraNote = paraText ? ` | parágrafo: "${paraText.slice(0, 60)}"` : ' | parágrafo próprio (sem texto)';
            const alt = (p.altTextDescription || '').trim();
            lines.push(`- imageId "inline-${i + 1}": ${fmtSize(p.width, p.height)} | ${where}${paraNote}` +
                (alt ? ` | alt: "${alt.slice(0, 60)}"` : ''));
        });
        floating.forEach((s) => {
            const alt = (s.altTextDescription || '').trim();
            lines.push(`- imageId "float-${s.id}": FLUTUANTE ${fmtSize(s.width, s.height)} | ` +
                `posição left=${Math.round(s.left)}pt top=${Math.round(s.top)}pt ` +
                `(rel. ${s.relativeHorizontalPosition}/${s.relativeVerticalPosition})` +
                (alt ? ` | alt: "${alt.slice(0, 60)}"` : ''));
        });
        if (!floatingShapesSupported()) {
            lines.push('(Este host não expõe imagens flutuantes — apenas as inline são manipuláveis aqui.)');
        }
        lines.push('Para mexer: resize_image (widthPt/heightPt ou scalePercent); move_image ' +
            '(flutuante: dxPt/dyPt ou leftPt/topPt; inline: anchorText); delete_image.');
        return lines.join('\n');
    });
}
export async function resizeImage(input) {
    const { widthPt, heightPt, scalePercent } = input;
    if (scalePercent === undefined && widthPt === undefined && heightPt === undefined) {
        throw new Error('Informe widthPt e/ou heightPt, ou scalePercent.');
    }
    if (scalePercent !== undefined && (scalePercent <= 0 || scalePercent > 1000)) {
        throw new Error('scalePercent deve estar entre 1 e 1000 (100 = tamanho atual).');
    }
    for (const [label, v] of [['widthPt', widthPt], ['heightPt', heightPt]]) {
        if (v !== undefined && (v < 4 || v > 2000)) {
            throw new Error(`${label} fora do intervalo razoável (4–2000pt). Recebi ${v}.`);
        }
    }
    return Word.run(async (context) => {
        const target = await resolveImage(context, input.imageId);
        const obj = target.kind === 'inline' ? target.pic : target.shape;
        obj.load('width,height,lockAspectRatio');
        await context.sync();
        const curW = obj.width;
        const curH = obj.height;
        let newW;
        let newH;
        if (scalePercent !== undefined) {
            newW = curW * (scalePercent / 100);
            newH = curH * (scalePercent / 100);
        }
        else if (widthPt !== undefined && heightPt !== undefined) {
            newW = widthPt;
            newH = heightPt;
        }
        else if (widthPt !== undefined) {
            newW = widthPt;
            newH = curH * (widthPt / curW); // mantém proporção
        }
        else {
            newH = heightPt;
            newW = curW * (heightPt / curH); // mantém proporção
        }
        // Distorção intencional (proporção diferente): destrava o aspect ratio
        // para o host não "corrigir" a segunda dimensão por baixo dos panos.
        const distorting = Math.abs(newW / newH - curW / curH) > 0.01;
        if (distorting && obj.lockAspectRatio)
            obj.lockAspectRatio = false;
        obj.width = newW;
        obj.height = newH;
        await context.sync();
        return (`Imagem ${input.imageId} redimensionada: ${fmtSize(curW, curH)} → ${fmtSize(newW, newH)}.` +
            (distorting ? ' (proporção alterada de propósito)' : ''));
    });
}
export async function moveImage(input) {
    return Word.run(async (context) => {
        const target = await resolveImage(context, input.imageId);
        if (target.kind === 'float') {
            const { dxPt, dyPt, leftPt, topPt } = input;
            if (dxPt === undefined && dyPt === undefined && leftPt === undefined && topPt === undefined) {
                throw new Error(`A imagem ${input.imageId} é FLUTUANTE: informe dxPt/dyPt (deslocamento) ` +
                    'ou leftPt/topPt (posição absoluta), em pontos.');
            }
            const shape = target.shape;
            shape.load('left,top');
            await context.sync();
            const oldLeft = shape.left;
            const oldTop = shape.top;
            const newLeft = leftPt !== undefined ? leftPt : oldLeft + (dxPt !== null && dxPt !== void 0 ? dxPt : 0);
            const newTop = topPt !== undefined ? topPt : oldTop + (dyPt !== null && dyPt !== void 0 ? dyPt : 0);
            shape.left = newLeft;
            shape.top = newTop;
            await context.sync();
            return (`Imagem ${input.imageId} movida: left ${Math.round(oldLeft)}→${Math.round(newLeft)}pt, ` +
                `top ${Math.round(oldTop)}→${Math.round(newTop)}pt.`);
        }
        // Inline: mover = realocar no fluxo do texto, junto a uma âncora.
        if (!input.anchorText) {
            throw new Error(`A imagem ${input.imageId} é INLINE (embutida no texto): ela não tem posição X/Y. ` +
                'Para movê-la, informe anchorText (+ anchorPosition before/after) apontando para ' +
                'o parágrafo de destino. dx/dy/left/top só valem para imagens flutuantes (float-*).');
        }
        const pic = target.pic;
        const oldPara = pic.paragraph;
        pic.load('width,height,altTextDescription,lockAspectRatio');
        oldPara.load('text');
        const b64Result = pic.getBase64ImageSrc();
        await context.sync();
        const base64 = (b64Result.value || '').replace(/^data:[^,]*,/, '');
        if (!base64)
            throw new Error('Não consegui extrair a imagem para realocá-la.');
        const spot = await resolveAnchoredSpot(context, input.anchorText, input.anchorPosition);
        // Nova casa primeiro; só então remove a original (nunca perder a imagem).
        // Estilo de corpo: criado logo depois de um título, o parágrafo herdaria o
        // estilo dele — e uma figura num parágrafo de título entra no Sumário.
        const newPara = spot.make('');
        applyHouseParagraphStyle(newPara, 'body_paragraph');
        newPara.alignment = Word.Alignment.centered;
        const newPic = newPara.insertInlinePictureFromBase64(base64, Word.InsertLocation.end);
        newPic.width = pic.width;
        newPic.height = pic.height;
        if (pic.altTextDescription)
            newPic.altTextDescription = pic.altTextDescription;
        await context.sync();
        const oldParaWasOnlyImage = !(oldPara.text || '').trim();
        pic.delete();
        if (oldParaWasOnlyImage) {
            try {
                oldPara.delete();
            }
            catch (_a) {
                /* último parágrafo do corpo pode não ser deletável — sem problema */
            }
        }
        await context.sync();
        return (`Imagem ${input.imageId} realocada para ${spot.where} ` +
            `(tamanho preservado).${spot.note} Os ids inline-N mudaram — rode list_images antes de mexer de novo.`);
    });
}
export async function deleteImage(input) {
    return Word.run(async (context) => {
        const target = await resolveImage(context, input.imageId);
        if (target.kind === 'float') {
            target.shape.delete();
            await context.sync();
            return `Imagem flutuante ${input.imageId} removida.`;
        }
        const pic = target.pic;
        const para = pic.paragraph;
        para.load('text');
        await context.sync();
        const paraWasOnlyImage = !(para.text || '').trim();
        pic.delete();
        if (paraWasOnlyImage) {
            try {
                para.delete();
            }
            catch (_a) {
                /* último parágrafo do corpo pode não ser deletável — sem problema */
            }
        }
        await context.sync();
        return (`Imagem ${input.imageId} removida` +
            (paraWasOnlyImage ? ' (junto com o parágrafo vazio que a continha)' : '') +
            '. Os ids inline-N mudaram — rode list_images antes de mexer em outra imagem.');
    });
}
// ======================================================================
// Revisão
// ======================================================================
export async function updateDocumentFields() {
    return Word.run(async (context) => {
        const updated = await updateDynamicFields(context);
        if (updated > 0)
            return `Campos dinâmicos atualizados (${updated}).`;
        return 'Nenhum campo dinâmico atualizável encontrado neste ambiente.';
    });
}
export async function addComment(input) {
    var _a;
    if (!wordApi14Supported())
        throw new Error(NEEDS_WORDAPI_14);
    if (!String((_a = input.comment) !== null && _a !== void 0 ? _a : '').trim())
        throw new Error('O texto do comentário está vazio.');
    return Word.run(async (context) => {
        const results = context.document.body.search(input.query, {});
        results.load('text');
        await context.sync();
        if (results.items.length === 0)
            throw new Error(`Trecho não encontrado: "${input.query}".`);
        const bodyFirst = await preferBodyRanges(context, results.items);
        const novo = bodyFirst[0].insertComment(input.comment);
        novo.load('id');
        await context.sync();
        // A busca não diferencia maiúsculas: "prazo" pode estar em 8 lugares, e o
        // comentário vai para o 1º do corpo. O retorno diz, para o modelo conferir.
        const varios = results.items.length > 1
            ? ` Ancorado na 1ª de ${results.items.length} ocorrências de "${briefText(input.query, 40)}" — ` +
                'se era outra, use um trecho mais longo e único.'
            : '';
        return `Comentário adicionado (commentId ${novo.id}).${varios}`;
    });
}
// ======================================================================
// Threads de comentário (RF08/RF09)
// ======================================================================
// Depende do requirement set WordApi 1.4. Em host antigo degrada com mensagem
// CLARA (mesmo padrão das shapes flutuantes, que exigem WordApiDesktop 1.2) em
// vez de estourar um erro cru do Office.js.
//
// Os ids de comentário são resolvidos NA HORA (mesma disciplina dos ids de
// imagem): o modelo lista, age, e relista se o documento mudou.
/** O host suporta comentários e controle de alterações? (WordApi 1.4) */
function wordApi14Supported() {
    try {
        return Office.context.requirements.isSetSupported('WordApi', '1.4');
    }
    catch (_a) {
        return false;
    }
}
const NEEDS_WORDAPI_14 = 'Recurso indisponível neste host: comentários exigem uma versão mais recente ' +
    'do Word (WordApi 1.4). Tudo o mais continua funcionando.';
// ---------------------------------------------------------------------------
// Controle de alterações — uso INTERNO apenas
// ---------------------------------------------------------------------------
// O produto não expõe mais controle de alterações: não há botão no painel nem
// ferramenta para o modelo (removidos a pedido). O que sobrou aqui é a defesa
// contra um estado que o USUÁRIO ainda pode ligar sozinho na aba Revisão do
// Word — e que, se ligado, transformaria "aplicar o template" e "Desfazer" numa
// revisão gigante e ilegível. Ver `withTrackChangesSuspended`.
/** Modo atual do controle de alterações ('Off', 'TrackAll', 'TrackMineOnly'); `null` sem a API. */
async function readTrackChangesMode() {
    if (!wordApi14Supported())
        return null;
    try {
        return await Word.run(async (context) => {
            var _a;
            const doc = context.document;
            doc.load('changeTrackingMode');
            await context.sync();
            return String((_a = doc.changeTrackingMode) !== null && _a !== void 0 ? _a : '') || null;
        });
    }
    catch (_a) {
        return null;
    }
}
/** Grava o modo do controle de alterações. Interno: só a suspensão usa. */
async function setTrackChangesMode(mode) {
    if (!wordApi14Supported())
        return;
    await Word.run(async (context) => {
        context.document.changeTrackingMode = mode;
        await context.sync();
    });
}
/**
 * Executa `fn` com o controle de alterações SUSPENSO, restaurando o estado
 * anterior no fim.
 *
 * Por que existe: operações que substituem o documento em MASSA — restaurar o
 * snapshot do "Desfazer" (`insertOoxml(replace)`) e aplicar o template
 * (`insertFileFromBase64`) — seriam registradas como uma revisão gigante e
 * ilegível se o tracking estivesse ligado (o Word marcaria o corpo inteiro como
 * apagado + reinserido). Elas representam "voltar/estabelecer um estado", não
 * uma edição de conteúdo a revisar. Best-effort: se ligar/desligar falhar, a
 * operação principal ainda roda.
 */
export async function withTrackChangesSuspended(fn) {
    // O modo EXATO volta no fim: reduzir a "ligado" e religar como "todas as
    // alterações" transformava o "só as minhas" do usuário em "de todos".
    const previous = await readTrackChangesMode();
    if (!previous || previous.toLowerCase() === 'off')
        return fn();
    try {
        await setTrackChangesMode(Word.ChangeTrackingMode.off);
    }
    catch (_a) {
        return fn(); // não conseguiu suspender: melhor executar do que abortar
    }
    try {
        return await fn();
    }
    finally {
        try {
            await setTrackChangesMode(previous);
        }
        catch (_b) {
            /* best-effort: o usuário pode reativar pela aba Revisão do Word */
        }
    }
}
/** Resolve um comentário pelo id, com erro instrutivo quando não existe. */
async function findComment(context, commentId) {
    const comments = context.document.body.getComments();
    comments.load('items/id');
    await context.sync();
    const hit = comments.items.find((c) => String(c.id) === String(commentId));
    if (!hit) {
        throw new Error(`Comentário "${commentId}" não encontrado: ele pode ter sido apagado. Rode ` +
            'list_comments de novo para ver as threads atuais.');
    }
    return hit;
}
/**
 * Teto de threads por página em `list_comments`. Existe para o modelo não pedir
 * uma página gigante e pagar a leitura de trecho ancorado + respostas de todas
 * as threads de uma vez; a cauda vem por `nextStartIndex`.
 */
const MAX_COMMENTS_PER_PAGE = 200;
/** RF08: lê as threads de comentário (comentário + respostas + trecho ancorado). */
export async function listComments(input) {
    if (!wordApi14Supported())
        throw new Error(NEEDS_WORDAPI_14);
    return Word.run(async (context) => {
        var _a, _b;
        const comments = context.document.body.getComments();
        comments.load('items/id,items/content,items/authorName,items/creationDate,items/resolved');
        await context.sync();
        const all = comments.items;
        // Leitura por JANELA, igual a read_document_text: sem isso, um documento em
        // revisão com mais threads que o teto ficava com a cauda INALCANÇÁVEL. O
        // cursor é a posição na lista COMPLETA (ver pageOfThreads): resolver threads
        // entre uma página e outra não desloca as que faltam.
        const max = Math.min(MAX_COMMENTS_PER_PAGE, Math.max(1, (_a = input.maxComments) !== null && _a !== void 0 ? _a : 50));
        const pagina = pageOfThreads(all.map((c) => !!c.resolved), !!input.includeResolved, (_b = input.startIndex) !== null && _b !== void 0 ? _b : 0, max);
        const visible = pagina.picked.map((i) => all[i]);
        const hasMore = pagina.nextStartIndex !== undefined;
        if (visible.length === 0) {
            return {
                commentCount: all.length,
                unresolvedCount: all.filter((c) => !c.resolved).length,
                matchingCount: pagina.matchingCount,
                comments: [],
                note: pagina.window.startIndex > 0 && pagina.matchingCount
                    ? `Nada a partir da posição ${pagina.window.startIndex}: as ${pagina.matchingCount} thread(s) que casam ficam antes dela.`
                    : all.length
                        ? 'Nenhum comentário aberto (use includeResolved=true para ver os resolvidos).'
                        : 'O documento não tem comentários.',
            };
        }
        // 2º lote: trecho ancorado + respostas de cada thread.
        const ranges = visible.map((c) => {
            const r = c.getRange();
            r.load('text');
            return r;
        });
        visible.forEach((c) => c.replies.load('items/content,items/authorName,items/creationDate'));
        await context.sync();
        // Região do trecho ancorado: diz ao modelo se dá para editar aquele texto
        // (capa/sumário são protegidos) — evita uma chamada que falharia.
        const regions = await regionsWithPrologue(context, ranges.map((r) => r.paragraphs.getFirst()));
        return Object.assign(Object.assign({ commentCount: all.length, unresolvedCount: all.filter((c) => !c.resolved).length, matchingCount: pagina.matchingCount, window: pagina.window, truncated: hasMore }, (hasMore
            ? {
                nextStartIndex: pagina.nextStartIndex,
                note: 'Há mais threads. Para seguir, chame de novo com ' +
                    `startIndex=${pagina.nextStartIndex} (a posição não muda se você resolver threads).`,
            }
            : {})), { comments: visible.map((c, i) => ({
                commentId: String(c.id),
                author: c.authorName || '',
                created: c.creationDate ? String(c.creationDate) : undefined,
                resolved: !!c.resolved,
                text: clipCommentText(c.content || ''),
                anchorText: briefText(ranges[i].text || '', 300),
                region: regions[i],
                replies: (c.replies.items || []).map((r) => ({
                    author: r.authorName || '',
                    text: clipCommentText(r.content || ''),
                    created: r.creationDate ? String(r.creationDate) : undefined,
                })),
            })) });
    });
}
/** RF08: responde DENTRO da thread do comentário (não cria comentário novo). */
export async function replyComment(input) {
    var _a;
    if (!wordApi14Supported())
        throw new Error(NEEDS_WORDAPI_14);
    if (!((_a = input.text) === null || _a === void 0 ? void 0 : _a.trim()))
        throw new Error('Resposta vazia.');
    return Word.run(async (context) => {
        const comment = await findComment(context, input.commentId);
        comment.reply(input.text);
        await context.sync();
        return `Resposta publicada na thread do comentário ${input.commentId}.`;
    });
}
/** RF08: marca a thread como resolvida (ou reabre com resolved=false). */
export async function resolveComment(input) {
    if (!wordApi14Supported())
        throw new Error(NEEDS_WORDAPI_14);
    return Word.run(async (context) => {
        const comment = await findComment(context, input.commentId);
        const target = input.resolved !== false;
        comment.resolved = target;
        await context.sync();
        return target
            ? `Comentário ${input.commentId} marcado como resolvido.`
            : `Comentário ${input.commentId} reaberto.`;
    });
}
const SEM_COMENTARIOS = { kept: 0, recreated: 0, lost: 0 };
/** O documento tem algum comentário? (Atalho: sem comentários, nada muda.) */
async function documentHasComments(context) {
    if (!wordApi14Supported())
        return false;
    try {
        const cs = context.document.body.getComments();
        cs.load('items/id');
        await context.sync();
        return cs.items.length > 0;
    }
    catch (_a) {
        return false;
    }
}
/**
 * Para cada parágrafo, se ele tem comentário ancorado (numa ida ao Word só).
 * `null` na lista = índice inexistente (o chamador já trata).
 */
async function paragraphsWithComments(context, paragraphs) {
    if (!(await documentHasComments(context)))
        return paragraphs.map(() => false);
    try {
        const cols = paragraphs.map((p) => {
            if (!p)
                return null;
            const c = p.getRange('Whole').getComments();
            c.load('items/id');
            return c;
        });
        await context.sync();
        return cols.map((c) => !!c && c.items.length > 0);
    }
    catch (_a) {
        return paragraphs.map((p) => !!p); // sem como saber: todos pelo caminho seguro
    }
}
/**
 * Região de cada parágrafo com a MESMA regra das ferramentas por índice
 * (read_document_text, replace_paragraphs): o prólogo da capa é capa, e depois
 * dele o que só PARECE capa pelo estilo é corpo. Sem isto, a folha de rosto de
 * um documento formatado era `cover` para list_comments e edit_commented_text a
 * recusava, enquanto replace_paragraphs a editava.
 */
async function regionsWithPrologue(context, paragraphs) {
    const regions = await loadParagraphRegions(context, paragraphs);
    try {
        const corpo = context.document.body.paragraphs;
        corpo.load('items/text');
        await context.sync();
        const fim = await coverPrologueEnd(context, corpo.items);
        if (fim <= 0)
            return [...regions]; // sem prólogo: como as ferramentas por índice
        const ultimo = corpo.items[fim - 1].getRange('Whole');
        const rel = paragraphs.map((p) => p.getRange('Whole').compareLocationWith(ultimo));
        await context.sync();
        const noPrologo = new Set(['Before', 'AdjacentBefore', 'OverlapsBefore', 'Equal', 'Inside', 'InsideStart', 'InsideEnd']);
        // Só o que vem DEPOIS do prólogo no fluxo do corpo deixa de ser capa. Texto
        // fora do fluxo (caixa de texto da capa, que a busca também acha) dá
        // "Unrelated" e fica com a região que já tinha.
        const depoisDoPrologo = new Set(['After', 'AdjacentAfter', 'OverlapsAfter']);
        return regions.map((r, i) => {
            var _a;
            const v = String(rel[i].value);
            if (noPrologo.has(v))
                return 'cover';
            return depoisDoPrologo.has(v) && r === 'cover' && ((_a = regions.coverByStyle) === null || _a === void 0 ? void 0 : _a[i]) ? 'body' : r;
        });
    }
    catch (_a) {
        return [...regions];
    }
}
/** Frase para o retorno quando threads saíram junto com o texto apagado. */
function removedThreadsNote(n) {
    return n ? ` ${n} thread(s) de comentário ancorada(s) no trecho saíram junto — diga isso ao usuário.` : '';
}
/**
 * Comentários cujo trecho está INTEIRO dentro de `target` — os que uma troca
 * de texto apagaria. (Um comentário cujo trecho passa dos limites do alvo
 * sobrevive à troca: parte do trecho continua lá.)
 */
async function commentsInside(context, target) {
    if (!wordApi14Supported())
        return [];
    try {
        const cs = target.getComments();
        cs.load('items/id');
        await context.sync();
        if (!cs.items.length)
            return [];
        const relacoes = cs.items.map((c) => c.getRange().compareLocationWith(target));
        await context.sync();
        const dentro = new Set(['Equal', 'Inside', 'InsideStart', 'InsideEnd']);
        return cs.items.filter((_, i) => dentro.has(String(relacoes[i].value)));
    }
    catch (_a) {
        return [];
    }
}
async function photographComments(context, comments) {
    comments.forEach((c) => {
        c.load('id,content,authorName,resolved');
        c.replies.load('items/content,items/authorName');
    });
    await context.sync();
    return comments.map((c) => ({
        id: String(c.id),
        content: c.content || '',
        author: c.authorName || '',
        resolved: !!c.resolved,
        replies: (c.replies.items || []).map((r) => ({ author: r.authorName || '', content: r.content || '' })),
    }));
}
/**
 * Índice do 1º caractere que a busca do Word acha sem surpresa (letra ou
 * número). Pontuação e `^` têm significado na busca do Word; letra não.
 */
function firstPlainCharIndex(text) {
    for (let i = 0; i < text.length; i++)
        if (/[0-9A-Za-zÀ-ÖØ-öø-ÿ]/.test(text.charAt(i)))
            return i;
    return -1;
}
/**
 * Troca o texto de `target` por `newText` sem perder os comentários ancorados
 * nele.
 *
 * `insertText(replace)` apaga o comentário cujo trecho some inteiro — o QA pediu
 * para manter e ele sumia mesmo assim. O que preserva a THREAD (verificado no
 * Word desktop: mesmo id, autor, data e respostas): escrever o texto novo DENTRO
 * do trecho comentado — depois do 1º caractere comum dele — e só então apagar o
 * texto antigo em volta. O comentário passa a ancorar o texto novo.
 *
 * Quando o alvo tem mais de um comentário, o primeiro leva o texto novo e os
 * demais, que sumiriam com o texto antigo, são RECRIADOS no texto novo, com o
 * conteúdo, o autor original e as respostas transcritos. O mesmo vale se o
 * primeiro não sobreviver (host que se comporta diferente). O que não der para
 * recriar é contado como perdido — e o retorno da ferramenta diz isso.
 */
async function replaceKeepingComments(context, target, newText) {
    const ameacados = await commentsInside(context, target);
    if (!ameacados.length) {
        target.insertText(newText, Word.InsertLocation.replace);
        await context.sync();
        return SEM_COMENTARIOS;
    }
    const fotos = await photographComments(context, ameacados);
    // A formatação que a troca simples daria ao texto novo: a do começo do ALVO.
    // Escrevendo por dentro do trecho comentado, o texto novo herdava a do trecho
    // — medido: termo comentado em negrito deixava o parágrafo inteiro em negrito.
    const formatoDoAlvo = await firstCharFont(context, target);
    let novo = null;
    try {
        const trecho = ameacados[0].getRange();
        trecho.load('text');
        await context.sync();
        const k = firstPlainCharIndex(trecho.text || '');
        if (k >= 0) {
            const achados = trecho.search(trecho.text.charAt(k), { matchCase: true });
            achados.load('items');
            await context.sync();
            const ponto = achados.items[0];
            novo = ponto.insertText(newText, Word.InsertLocation.after);
            await context.sync();
            if (formatoDoAlvo)
                await applyFontDelta(context, novo, formatoDoAlvo);
            // Cauda primeiro (do fim do texto novo ao fim do alvo), depois a cabeça
            // (do início do alvo até o ponto): assim um corte não desloca o outro.
            novo.getRange('After').expandTo(target.getRange('End')).delete();
            await context.sync();
            target.getRange('Start').expandTo(ponto.getRange('End')).delete();
            await context.sync();
        }
    }
    catch (e) {
        console.warn('[replaceKeepingComments] escrita por dentro falhou; troca simples:', e);
        novo = null;
    }
    if (!novo) {
        novo = target.insertText(newText, Word.InsertLocation.replace);
        await context.sync();
    }
    // Conferência: quem sobreviveu? O resto é recriado no texto novo.
    const agora = context.document.body.getComments();
    agora.load('items/id');
    await context.sync();
    const vivos = new Set(agora.items.map((c) => String(c.id)));
    const mortos = fotos.filter((f) => !vivos.has(f.id));
    const recriacao = await recreateComments(context, mortos, novo);
    return { kept: fotos.length - mortos.length, recreated: recriacao.recreated, lost: recriacao.lost };
}
/**
 * Recria threads (conteúdo, respostas, estado resolvido) ancoradas em `on`.
 * O Office.js não deixa gravar o autor nem a data: a thread recriada fica em
 * nome de quem está usando o mIAu, e o autor original vai citado no texto —
 * por isso o retorno avisa (commentOutcomeNote).
 */
async function recreateComments(context, fotos, on) {
    const out = { kept: 0, recreated: 0, lost: 0 };
    for (const f of fotos) {
        try {
            const recriado = on.insertComment(`${f.content}\n\n(Comentário original de ${f.author || 'autor desconhecido'}, reancorado pelo mIAu.)`);
            for (const r of f.replies)
                recriado.reply(`${r.author ? `${r.author}: ` : ''}${r.content}`);
            if (f.resolved)
                recriado.resolved = true;
            await context.sync();
            out.recreated++;
        }
        catch (e) {
            console.warn('[recreateComments] não consegui recriar o comentário:', e);
            out.lost++;
        }
    }
    return out;
}
/** Propriedades de caractere que a troca simples herdaria do começo do alvo. */
const FONT_PROPS = [
    'bold', 'italic', 'underline', 'strikeThrough', 'superscript', 'subscript',
    'name', 'size', 'color', 'highlightColor',
];
/** Formatação do 1º caractere comum de `target` (ou `null` se não houver). */
async function firstCharFont(context, target) {
    try {
        target.load('text');
        await context.sync();
        const k = firstPlainCharIndex(target.text || '');
        if (k < 0)
            return null;
        const hits = target.search(target.text.charAt(k), { matchCase: true });
        hits.load('items');
        await context.sync();
        const c = hits.items[0];
        if (!c)
            return null;
        c.font.load(FONT_PROPS.join(','));
        await context.sync();
        const f = c.font;
        const out = {};
        for (const p of FONT_PROPS)
            out[p] = f[p];
        return out;
    }
    catch (_a) {
        return null;
    }
}
/** Aplica em `range` só as propriedades que diferem de `ref`. */
async function applyFontDelta(context, range, ref) {
    try {
        range.font.load(FONT_PROPS.join(','));
        await context.sync();
        const fonte = range.font;
        let mudou = false;
        for (const p of FONT_PROPS) {
            const quero = ref[p];
            // highlightColor null = sem realce, e isso é um valor a reaplicar; nos
            // demais, null/undefined é "misto/indisponível" e não se toca.
            if (quero === undefined || (quero === null && p !== 'highlightColor'))
                continue;
            if (fonte[p] !== quero) {
                fonte[p] = quero;
                mudou = true;
            }
        }
        if (mudou)
            await context.sync();
    }
    catch (e) {
        console.warn('[applyFontDelta] não consegui acertar a formatação do texto novo:', e);
    }
}
/** Frase para o retorno da ferramenta (vazia quando não havia comentário). */
function commentOutcomeNote(o) {
    const partes = [
        o.kept ? `${o.kept} comentário(s) mantido(s), agora ancorado(s) no texto novo` : '',
        o.recreated
            ? `${o.recreated} recriado(s) no texto com o conteúdo e as respostas, mas EM NOME DO ` +
                'USUÁRIO (o Word não deixa gravar o autor original, que ficou citado no comentário) — avise'
            : '',
        o.lost ? `${o.lost} PERDIDO(S) — avise o usuário` : '',
    ].filter(Boolean);
    return partes.length ? ` Comentários no trecho: ${partes.join('; ')}.` : '';
}
/** RF09: reescreve o TEXTO ANCORADO a um comentário (o trecho comentado). */
export async function editCommentedText(input) {
    if (!wordApi14Supported())
        throw new Error(NEEDS_WORDAPI_14);
    if (input.newText == null)
        throw new Error('newText é obrigatório.');
    return Word.run(async (context) => {
        const comment = await findComment(context, input.commentId);
        const range = comment.getRange();
        range.load('text');
        await context.sync();
        // O trecho comentado pode estar em região protegida (capa/sumário) — com a
        // mesma regra de prólogo de list_comments e replace_paragraphs.
        const paragraph = range.paragraphs.getFirst();
        await assertEditableParagraph(context, paragraph, 'Edição do texto comentado');
        const before = briefText(range.text || '', 80);
        // `insertText(replace)` sobre o trecho inteiro APAGAVA o comentário (visto
        // no Word). A troca por dentro mantém a thread ancorada no texto novo.
        const comentarios = await replaceKeepingComments(context, range, input.newText);
        const neighborhood = await resultNeighborhood(context, paragraph);
        return (`Texto comentado reescrito (comentário ${input.commentId}). Antes: «${before}».` +
            commentOutcomeNote(comentarios) +
            neighborhood);
    });
}
// ======================================================================
// Registry + dispatcher
// ======================================================================
/**
 * Insere no DOCUMENTO uma imagem que o usuário anexou no chat.
 *
 * O anexo de imagem tinha um caminho só: virar bloco nativo para o modelo VER a
 * figura. Não havia como escrevê-la no Word — o usuário anexava um PNG, o
 * modelo descrevia a imagem e não conseguia colá-la. Os bytes vêm do registro
 * em `util/attachments.ts` (memória do painel), não do documento.
 *
 * Posicionamento e proteção de região seguem as demais inserções: âncora de
 * parágrafo real, capa/sumário bloqueados.
 */
async function insertImage(input) {
    const img = findInsertableImage(input.name);
    if (!img) {
        const disponiveis = listInsertableImages();
        throw new Error(disponiveis.length
            ? `Não achei a imagem "${input.name}". Anexadas nesta conversa: ` +
                `${disponiveis.map((i) => `"${i.name}"`).join(', ')}.`
            : 'Nenhuma imagem anexada nesta conversa. Peça ao usuário para anexar o ' +
                'arquivo pelo clipe do chat antes de inserir.');
    }
    return Word.run(async (context) => {
        var _a, _b;
        const loc = (_a = input.location) !== null && _a !== void 0 ? _a : 'endOfDocument';
        const body = context.document.body;
        let host;
        let where;
        let nota = '';
        if (input.anchorText) {
            // "No fim do tópico" é `endOfSection`: com `after` no título a figura ia
            // parar no começo do tópico, logo abaixo do título (visto pelo QA).
            const spot = await resolveAnchoredSpot(context, input.anchorText, input.anchorPosition);
            host = spot.make('');
            where = spot.where;
            nota = spot.note;
        }
        else if (loc === 'startOfDocument') {
            await assertEditableLocation(context, loc, 'Inserção de imagem');
            const start = await resolveStartOfBodyContent(context);
            if (start) {
                host = insertParagraphAtBodyStart(start, '');
                where = bodyStartWhere(start.skipped);
            }
            else {
                host = body.insertParagraph('', Word.InsertLocation.start);
                where = loc;
            }
        }
        else if (loc === 'afterSelection' || loc === 'replaceSelection') {
            await assertEditableLocation(context, loc, 'Inserção de imagem');
            host = context.document.getSelection().insertParagraph('', Word.InsertLocation.after);
            where = loc;
        }
        else {
            const placement = await resolveEndOfDocumentInsertion(context);
            if (placement.reuse) {
                host = placement.reuse;
                where = 'endOfDocument (parágrafo vazio final reaproveitado)';
            }
            else {
                host = body.insertParagraph('', Word.InsertLocation.end);
                if (placement.breakAfterToc) {
                    host.insertBreak(Word.BreakType.page, Word.InsertLocation.before);
                    where = 'endOfDocument (nova página aberta após o Sumário)';
                }
                else {
                    where = loc;
                }
            }
        }
        // Figura é centralizada no padrão Norven (estilo "Figura" do template). O
        // estilo de corpo vem antes: um parágrafo criado logo depois de um título
        // herdaria o estilo dele, e a figura entraria no Sumário como "título".
        applyHouseParagraphStyle(host, 'body_paragraph');
        host.alignment = Word.Alignment.centered;
        const pic = host.insertInlinePictureFromBase64(img.dataBase64, Word.InsertLocation.end);
        // altText: acessibilidade e rastreabilidade da origem do arquivo.
        pic.altTextDescription = img.name;
        if (typeof input.widthPt === 'number' && input.widthPt > 0) {
            pic.lockAspectRatio = true;
            pic.width = input.widthPt;
        }
        await context.sync();
        let legenda = '';
        if ((_b = input.caption) === null || _b === void 0 ? void 0 : _b.trim()) {
            const cap = host.insertParagraph(input.caption.trim(), Word.InsertLocation.after);
            cap.alignment = Word.Alignment.centered;
            await context.sync();
            legenda = ' Legenda inserida abaixo.';
        }
        const neighborhood = await resultNeighborhood(context, host);
        return (`Imagem "${img.name}" inserida ${where}.${nota}${legenda}` +
            ' Use list_images + resize_image/move_image para ajustá-la.' +
            neighborhood);
    });
}
/**
 * Atualiza o PLANO de tópicos (esqueleto) do documento. Não toca no corpo: só
 * o metadado que aparece no contexto de cada turno. Recebe a lista completa e
 * final; `normalizeSkeleton` tira numeração digitada, vazios e espaços.
 */
async function setDocumentSkeleton(input) {
    const topics = await setSkeleton(input === null || input === void 0 ? void 0 : input.topics);
    if (!topics.length) {
        return 'Plano de tópicos removido. O documento segue sem esqueleto planejado.';
    }
    const list = topics.map((t, i) => `${i + 1}. ${t}`).join('\n');
    return `Plano de tópicos atualizado (${topics.length}):\n${list}`;
}
const registry = {
    // leitura
    get_document_overview: () => getDocumentOverview(),
    read_document_text: (i) => readDocumentText(i),
    read_selection: () => readSelection(),
    search_text: (i) => searchText(i),
    list_content_controls: () => listContentControls(),
    // escrita: texto
    insert_paragraph: (i) => insertParagraph(i),
    insert_text: (i) => insertText(i),
    replace_text: (i) => replaceText(i),
    replace_paragraphs: (i) => replaceParagraphs(i),
    delete_text: (i) => deleteText(i),
    delete_paragraphs: (i) => deleteParagraphs(i),
    move_text: (i) => moveText(i),
    fill_content_control: (i) => fillContentControl(i),
    // escrita: estrutura
    insert_table: (i) => insertTable(i),
    insert_list: (i) => insertList(i),
    delete_table: (i) => deleteTable(i),
    // escrita: formatação
    apply_formatting: (i) => applyFormatting(i),
    apply_paragraph_style: (i) => applyParagraphStyle(i),
    apply_paragraph_styles: (i) => applyParagraphStyles(i),
    // imagens (objetos)
    list_images: () => listImages(),
    resize_image: (i) => resizeImage(i),
    move_image: (i) => moveImage(i),
    delete_image: (i) => deleteImage(i),
    // revisão
    update_document_fields: () => updateDocumentFields(),
    add_comment: (i) => addComment(i),
    // threads de comentário (RF08/RF09)
    list_comments: (i) => listComments(i),
    reply_comment: (i) => replyComment(i),
    resolve_comment: (i) => resolveComment(i),
    edit_commented_text: (i) => editCommentedText(i),
    // plano de tópicos (metadado do documento, não escreve no corpo)
    set_document_skeleton: (i) => setDocumentSkeleton(i),
    // imagem anexada no chat -> documento
    insert_image: (i) => insertImage(i),
};
// Ferramentas de escrita em MÚLTIPLOS passos: um erro no meio pode deixar
// conteúdo parcialmente aplicado (Office.js executa o lote até a operação que
// falha; não há rollback). Repetir a chamada às cegas DUPLICA o que já entrou —
// foi exatamente assim que retries de insert_list geraram itens repetidos.
// apply_paragraph_styles não duplica texto, mas para no meio de um lote: parte
// dos parágrafos pode já estar no estilo novo.
const PARTIAL_WRITE_TOOLS = new Set(['insert_list', 'insert_table', 'apply_paragraph_styles']);
export async function executeTool(name, input) {
    const handler = registry[name];
    if (!handler)
        return { content: `Ferramenta desconhecida: ${name}`, is_error: true };
    try {
        const out = await handler(input !== null && input !== void 0 ? input : {});
        return { content: typeof out === 'string' ? out : JSON.stringify(out) };
    }
    catch (e) {
        const partialNote = !PARTIAL_WRITE_TOOLS.has(name)
            ? ''
            : name === 'apply_paragraph_styles'
                ? ' ATENÇÃO: parte do lote pode já estar no estilo novo (não há rollback). Confira com ' +
                    'read_document_text e reenvie só os parágrafos que faltaram.'
                : ' ATENÇÃO: a operação pode ter sido aplicada PARCIALMENTE (não há rollback). ' +
                    'NÃO repita a chamada às cegas: primeiro confira com read_document_text o que ' +
                    'já entrou no documento e remova o conteúdo parcial antes de tentar de novo.';
        return { content: `Erro em ${name}: ${errMessage(e)}${partialNote}`, is_error: true };
    }
}
